import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import type { GroupBase } from "react-select";
import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Product } from "types";
import { Variant } from "types";
import CustomFieldBuilder from "components/CustomFieldBuilder";
import DynamicForm from "components/DynamicForm";
import { CustomField, Warehouse, Media } from "types";
import { nameValidation, statusValidation } from "validationSchemas/common";
import { T } from "assets/libs/chart.js/chunks/helpers.segment";
import { showSuccess, showError, showInfo } from "toast/toast";
import { MobileCardsOnly } from "components/TableandMobilecomponent";
import { UIInputFF } from "components/Adapter";
import { UIReactSelect } from "components/WarehouseInputfields";
import { fetchBrandsByUserSelect2, fetchCategoriesByUserSelect2 } from "api/postTypeApiCalls";
import { Package, Settings } from 'lucide-react';
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import { DeleteIconButton, EditIconButton } from "components/DatatablesActionIcons";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import SignageVolumeDiscount, { type VolumeDiscountUI } from "./SignageVolumeDiscount";
// import type { VolumeDiscount } from "components/Volumediscountcalls";
// import SignageVolumeDiscount, {
//   VolumeDiscount,
//   VolumeDiscountCombination,
// } from "./SignageVolumeDiscount";
import DesignsTab, { type ProductCombination } from "components/DesignsTab";
import { handleApiError } from "utils/errorHandler";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { getAuthCookie } from "../../utils/authCookies";
type FormValues = {
  name: string;
  product_type: string;
  sku: string;
  description: string;
  brand: string;
  brand_id: string;
  category: string;
  category_id: string;
  item_condition: string;
  condition_note: string;
  purchase_price: string | number;
  retail_price: string | number;
  length: string | number;
  height: string | number;
  width: string | number;
  weight: string | number;
  threshold_qty: string | number;
  attribute: string;
  parameter: string;
  images: File[] | null;
  statuss: string;
  is_rotational: string;
  is_production: string;
  warehouseId: string;
  on_hand: string;
  available: string;
  reserved: string;
  is_taxable: boolean;
  tax_rule_id: string;
  taxRule: TaxRule | null;
  taxGroup: TaxGroup | null;
  shipping_config: "global" | "per_variant";
};

type Image = {
  img_url: string;
}

type Brand = {
  id: string;
  name: string;
  image: string;
  status: string;
};

type Category = {
  id: string;
  name: string;
  image: string;
  status: string;
};
type Attribute = {
  id: string;
  name: string;
  values: string[];
  description: string;
  status: string;
};

type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type TaxRule = {
  _id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

type CombRow = {
  sku?: string;
  attributes: Record<string, string>;
  price: number;
};

type WarehouseRow = {
  warehouse_id: string;
  warehouse_name: string;
  zone_id: string;
  zone_name: string;
  rack_id: string;
  rack_name: string;
  bin_id: string;
  bin_name: string;
  on_hand: number;
  available: number;
  reserved: number;
};
export type CombinationRow = {
  id?: string;
  variant_name: string;
  sku: string;
  description: string;
  attributes: Record<string, string>;
  price: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  // quantity: number;
  warehouseData: WarehouseRow[];
  imageIds?: string[];
  imageLocalKeys?: string[];
  variant_id?: string;
};

// export type CombinationRow = {
//   id: string;
//    variant_name: string;
//   sku: string;
//   description: string;
//   quantity: number;
//   attributes: Record<string, string>;
//   price: number;
//   length: number;
//   width: number;
//   height: number;
//   weight: number;
//   warehouseData: WarehouseRow[];
//   imageIds?: string[];
//   variant_id?: string;
// };
type VolumeDiscountCombination = {
  id?: string;
  label?: string;
  attributes?: Record<string, string>;
};
type VariantApiItem = {
  variant_id?: string;
  id?: string;
  variant_name?: string;
  sku?: string;
  attributes?: Record<string, any>;
  // ... other fields from the API you don't need here
};


// ---------- Types ----------
export type VolumeDiscount = {
  min_qty: number;
  max_qty?: number;
  price?: number; // absolute price for this tier (optional)
  percent?: number; // percent off (optional)
};


// maps backend boolean to UI radio string
const toConfigString = (flag?: boolean | null): "global" | "per_variant" =>
  flag ? "global" : "per_variant";


const columnHelper = createColumnHelper<CombRow>();
const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";
const SignageEditVariableProduct = () => {
  const { productId } = useParams<{ productId: string }>();
  // const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // State for parent signage firm (for SignageTeammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  // signageId: parent signage firm when SignageTeammember, else userId
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : (userId || "");
  const signageName = (isSignageTeammember && parentSignageName) ? parentSignageName : "";

  // Fetch parent hierarchy for SignageTeammember login
  useEffect(() => {
    const loadParentHierarchy = async () => {
      if (isSignageTeammember && token) {
        const hierarchyData = await fetchParentHierarchy(token);
        if (hierarchyData) {
          setParentSignageId(hierarchyData.parentSignageId);
          setParentSignageName(hierarchyData.parentSignageName);
        }
      }
    };
    loadParentHierarchy();
  }, [isSignageTeammember, token]);

  // ✅ Use the brand-category selector hook with signageId (parent firm when SignageTeammember)
  const {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions: brandOptionsFromHook,
    filteredCategories: categoryOptionsFromHook,
    loadingBrands,
    loadingCategories,
    brandCategoryIds,
  } = useBrandCategorySelector(signageId || userId || "", token || "");
  
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [options, setOptions] = useState<Record<string, string>[]>([]);
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>([]);
  const [taxRules, setTaxRules] = useState<TaxRule[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[] | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // IDs the user removed in UI; we will delete them on Save
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [combinationsfetch, setCombinationsFetch] = useState<VariantApiItem[]>([]);
  // const [volumeDiscounts, setVolumeDiscounts] = useState<VolumeDiscount[]>([]);
  const [volumeDiscounts, setVolumeDiscounts] = useState<VolumeDiscountUI[]>([]);
  const [variables, setVariables] = useState<ProductCombination[]>([]);

  const [locationOptions, setLocationOptions] = useState<{
    [warehouseId: string]: {
      zones: any[];
      racks: any[];
      bins: any[];
    };
  }>({});

  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);


  const [selectedWarehouseName, setSelectedWarehouseName] = useState<
    string | null
  >(null);

  // State for toggling warehouse visibility
  const [showWarehouseIndex, setShowWarehouseIndex] = useState<number | null>(
    null
  );


  type UploadedImage = { id: string; name: string; size: number };
  type PendingImage = { file: File; previewUrl: string };

  const [uploaded, setUploaded] = useState<UploadedImage[]>([]);        // all uploaded (deduped) images
  const [pendingFiles, setPendingFiles] = useState<PendingImage[]>([]); // ✅ Files waiting to be uploaded
  const [imagesPreparing, setImagesPreparing] = useState(false); // ✅ Loading state for image upload

  // Build a tiny pseudo-payload so `useVariantImageUrlMap` can presign
  const dataForMap = useMemo(() => {
    if (uploaded.length === 0) return [];
    return [
      {
        product: {
          variants: [
            { images: uploaded.map(img => ({ url: img.id })) } // {url: <24hex>}
          ],
        },
      },
    ];
  }, [uploaded]);

  // Presigned URLs for all *uploaded* ids
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // OPTIONAL: log when map is ready
  // useEffect(() => {
  //   safeConsole.log("presigned URLs", imageUrlMap);
  // }, [imageUrlMap]);

  // ✅ Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      pendingFiles.forEach(p => URL.revokeObjectURL(p.previewUrl));
    };
  }, [pendingFiles]);

  // ✅ Modified: Just add to preview state, don't upload yet
  const onPickFiles = (files: File[]) => {
    if (!files?.length) return;

    // de-dup based on (name|size) against both uploaded and pending
    const existingKeys = new Set([
      ...uploaded.map(u => `${u.name}|${u.size}`),
      ...pendingFiles.map(p => `${p.file.name}|${p.file.size}`)
    ]);
    const fresh = files.filter(f => !existingKeys.has(`${f.name}|${f.size}`));

    if (fresh.length === 0) {
      showInfo("All selected images were already added.");
      return;
    }

    // ✅ Create preview URLs and add to pending state (don't upload yet)
    const newPending: PendingImage[] = fresh.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file)
    }));

    setPendingFiles(prev => [...prev, ...newPending]);
    showInfo(`${fresh.length} image(s) added. Click "Save Images" to upload them.`);
  };

  // ✅ Remove uploaded image (mark for deletion on save)
  const removeUploadedImage = (imageId: string) => {
    // Add to pending deletes
    setPendingDeleteIds(prev => Array.from(new Set([...(prev || []), imageId])));

    // Remove from uploaded gallery
    setUploaded(prev => prev.filter(u => u.id !== imageId));

    // Also strip from all combinations selections
    setCombinations(prev =>
      prev.map(c => ({ ...c, imageIds: (c.imageIds || []).filter(id => id !== imageId) }))
    );

    showInfo("Image marked for deletion. It will be deleted when you save the product.");
  };

  // ✅ Remove pending image (not uploaded yet)
  const removePendingImage = (previewUrl: string) => {
    setPendingFiles(prev => {
      const item = prev.find(p => p.previewUrl === previewUrl);
      if (item) {
        URL.revokeObjectURL(item.previewUrl); // Clean up blob URL
      }
      return prev.filter(p => p.previewUrl !== previewUrl);
    });
    showInfo("Image removed from preview.");
  };

  // ✅ Upload pending images and get their IDs
  const handlePrepareProductImages = async () => {
    if (imagesPreparing) return;
    setImagesPreparing(true);

    try {
      // 1) Delete marked images first
      if (pendingDeleteIds.length > 0) {
        try {
          await hardDeletePresignedFiles(pendingDeleteIds, token || null);
          setPendingDeleteIds([]); // clear
        } catch (delErr: any) {
          safeConsole.error("Image hard delete (batch) failed:", delErr);
          // Don't block success — just inform
          showError(delErr?.message || "Some removed images could not be deleted.");
        }
      }

      // 2) Upload newly picked images (if any)
      const stagedFiles = pendingFiles.map((p) => p.file);
      let stagedIds: string[] = [];
      if (stagedFiles.length) {
        if (!token) throw new Error("Not authenticated");
        stagedIds = await uploadFilesAndGetIds(stagedFiles, token);
      }

      // 3) Build merged uploaded list
      const stagedUploaded: UploadedImage[] = stagedIds.map((id, i) => ({
        id,
        name: stagedFiles[i].name,
        size: stagedFiles[i].size,
      }));
      const allUploaded = [...uploaded, ...stagedUploaded];

      // 4) Clear preview URLs
      pendingFiles.forEach(p => {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch { }
      });
      setPendingFiles([]);

      // 5) Update uploaded state
      setUploaded(allUploaded);

      showSuccess(`${stagedIds.length} image(s) uploaded successfully!`);
    } catch (e: any) {
      safeConsole.error(e);
      showError(e?.message || "Failed to upload images.");
    } finally {
      setImagesPreparing(false);
    }
  };


  // Function to toggle warehouse inputs
  const toggleWarehouses = (index: number) => {
    setShowWarehouseIndex((prev) => (prev === index ? null : index));
  };

  const toggleEditVariant = (index: number) => {
    setShowWarehouseIndex((prev) => (prev === index ? null : index));
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null); // To track which combination is being edited
  const [combErrors, setCombErrors] = useState<{ zone: boolean; rack: boolean; bin: boolean }[][]>([]);
  const [deletingIds, setDeletingIds] = React.useState<Set<string | number>>(new Set());
  // Temporary state for imageIds while editing (only applied on Save)
  const [tempImageIds, setTempImageIds] = useState<string[] | null>(null);

  // ✅ Refs to track pending API calls and prevent duplicates
  const fetchingZonesRef = React.useRef<Set<string>>(new Set());
  const fetchingRacksRef = React.useRef<Set<string>>(new Set());
  const fetchingBinsRef = React.useRef<Set<string>>(new Set());

  // Transform variant data to ProductCombination format for DesignsTab
  const transformVariantsToCombinations = (variants: any[]): ProductCombination[] => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return [];
    }
    return variants.map((variant: any, index: number) => ({
      id: variant.variant_id || variant._id || variant.id || String(index),
      sno: index + 1,
      attributes: variant.attributes || {},
      price: variant.retail_price || 0,
      variant_name: variant.variant_name || "",
    } as ProductCombination & { variant_name?: string }));
  };

  async function fetchHierarchy(payload: object) {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to fetch hierarchy");
      return res.json();
    } catch (error: any) {
      await handleApiError(error, {
        module: "inventory",
        action: "Signage_Edit_Variable_Product_fetch_hierarchy",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/warehouses/hierarchy`,
        method: "POST",
        requestBody: payload,
      }, token);
      throw error;
    }
  }

  // useEffect(() => {
  //   async function loadLocationOptions() {
  //     if (!isEditMode || editIndex === null) return;
  //     const opts: typeof locationOptions = {};
  //     for (const wh of warehouses) {
  //       opts[wh._id] = { zones: [], racks: [], bins: [] };
  //       try {
  //         const zoneData = await fetchHierarchy({ warehouse_id: wh._id });
  //         opts[wh._id].zones = zoneData.zones || [];

  //         const row = combinations[editIndex].warehouseData.find(wd => wd.warehouse_id === wh._id);
  //         if (row?.zone_id) {
  //           const rackData = await fetchHierarchy({ zone_id: row.zone_id });
  //           opts[wh._id].racks = rackData.racks || [];

  //           if (row.rack_id) {
  //             const binData = await fetchHierarchy({ rack_id: row.rack_id });
  //             opts[wh._id].bins = binData.bins || [];
  //           }
  //         }
  //       } catch { }
  //     }
  //     setLocationOptions(opts);
  //   }
  //   loadLocationOptions();
  //   // eslint-disable-next-line
  // }, [isEditMode, editIndex, warehouses]);

  const handleZoneChange = async (
    zone_id: string,
    zone_name: string,
    warehouse_id: string,
    ci: number,
    wi: number,
    editIndex: any,
  ) => {
    try {
      const rackData = await fetchHierarchy({ zone_id });

      setLocationOptions(prev => ({
        ...prev,
        [warehouse_id]: {
          ...prev[warehouse_id],
          racks: rackData.racks || [],
          bins: [],
        },
      }));

      setCombinations(prev =>
        prev.map((c, i) =>
          i === ci
            ? {
              ...c,
              warehouseData: c.warehouseData.map((w, j) =>
                j === wi
                  ? {
                    ...w,
                    zone_id,
                    zone_name,
                    rack_id: '',
                    rack_name: '',
                    bin_id: '',
                    bin_name: '',
                  }
                  : w
              ),
            }
            : c
        )
      );
    } catch (err) {
      safeConsole.error("Failed to load racks:", err);
    }
  };

  const handleRackChange = async (
    rack_id: string,
    rack_name: string,
    warehouse_id: string,
    ci: number,
    wi: number,
    editIndex: any,
  ) => {
    // ✅ Guard: Don't process if rack_id is empty
    if (!rack_id) return;

    // ✅ Check if this rack is already the current one
    const currentRackId = combinations[ci]?.warehouseData?.[wi]?.rack_id || "";
    if (rack_id === currentRackId) return;

    // ✅ Check if already fetching bins for this rack
    const isFetching = fetchingBinsRef.current.has(rack_id);
    if (isFetching) return;

    // ✅ Update state first
    setCombinations(prev =>
      prev.map((c, i) =>
        i === ci
          ? {
            ...c,
            warehouseData: c.warehouseData.map((w, j) =>
              j === wi
                ? {
                  ...w,
                  rack_id,
                  rack_name,
                  bin_id: '',
                  bin_name: '',
                }
                : w
            ),
          }
          : c
      )
    );

    // ✅ Always fetch bins for selected rack (when rack changes, even if bins were previously loaded)
    fetchingBinsRef.current.add(rack_id);
    try {
      const binData = await fetchHierarchy({ rack_id });

      setLocationOptions(prev => ({
        ...prev,
        [warehouse_id]: {
          ...prev[warehouse_id],
          bins: binData.bins || [],
        },
      }));
    } catch (err) {
      safeConsole.error("Failed to load bins:", err);
    } finally {
      fetchingBinsRef.current.delete(rack_id);
    }
  };

  // Fetch product data

  const fetchProduct = async () => {
    if (!token || !signageId) {
      safeConsole.error("Missing token or signageId");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}?permissions=false`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch product data");
      }

      const data = await response.json();
      // safeConsole.log("Fetched product data:", data);

      // setProduct(data);
      setProductObj(data);
      // 1) collect all image IDs (product-level + variant-level)
      const byId = new Map<string, { id: string; name: string; size: number }>();

      (data.product?.images || []).forEach((img: any) => {
        if (img?.url) byId.set(img.url, { id: img.url, name: img.alt_text || img.url, size: 0 });
      });
      (data.product?.variants || []).forEach((v: any) => {
        (v.images || []).forEach((img: any) => {
          if (img?.url) byId.set(img.url, { id: img.url, name: img.alt_text || img.url, size: 0 });
        });
      });

      const collected = Array.from(byId.values());

      // 2) de-dup when merging into existing uploaded
      setUploaded(prev => {
        const seen = new Set(prev.map(p => p.id));
        const add = collected.filter(x => !seen.has(x.id));
        return [...prev, ...add];
      });

      setUploadedFileMeta(data.product.images);
      if (data.product.variants && data.product.variants.length > 0) {
        setVariants(data.product.variants);
        const customizationFields = data.product.variants[0]?.customizations?.fields || [];

        if (customizationFields.length > 0) {
          setCustomFields(
            customizationFields.map((field: any, index: number) => ({
              id: index + 1,
              label: field.field_name,
              type: field.field_type,
              required: field.required,
            }))
          );
        }
        // safeConsole.log("Custom fields:", customizationFields);
        
        // Transform variants to ProductCombination format for DesignsTab
        const transformedVariables = transformVariantsToCombinations(data.product.variants);
        setVariables(transformedVariables);
      }

    } catch (err: any) {
      await handleApiError(err, {
        module: "inventory",
        action: "Signage_Edit_Variable_Product_fetch_product",
        userId: signageId || undefined,
        licenseType: licenseType || undefined,
        userName: signageName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}?permissions=false`,
        method: "GET",
      }, token);
      safeConsole.error("Error fetching product data:", err);
      toast.error("Error fetching product data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if (productId) fetchProduct();
  }, [productId, token, signageId]);



  // ✅ Brands and categories are now loaded via useBrandCategorySelector hook above
  // No separate fetching needed!

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setWarehouses(data);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Edit_Variable_Product_fetch_warehouses",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/product-attributes?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setAttributes(data);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Edit_Variable_Product_fetch_attributes",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/product-attributes?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [token]);

  useEffect(() => {
    const fetchTaxGroups = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/tax-groups?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setTaxGroups(data);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Edit_Variable_Product_fetch_tax_groups",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/tax-groups?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTaxGroups();
  }, [token]);

  useEffect(() => {
    const fetchTaxRules = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/tax-rules?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setTaxRules(data);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Edit_Variable_Product_fetch_tax_rules",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/tax-rules?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTaxRules();
  }, [token]);



  useEffect(() => {
    if (variants && variants.length > 0) {
      const preselectedOptions = variants.flatMap((variant) =>
        Object.entries(variant.attributes).map(([key, value]) => ({
          [key.toLowerCase()]: value,
        }))
      );
      setOptions(preselectedOptions);
    }
  }, [variants]);

  const handleAttributeClick = (attribute: string, value: string) => {
    setOptions((prevOptions) => {
      const key = attribute.toLowerCase();
      const existingIndex = prevOptions?.findIndex(
        (option) => option[key] === value
      );

      if (existingIndex !== -1) {
        // If the attribute-value pair exists, remove it
        return prevOptions?.filter((_, index) => index !== existingIndex);
      } else {
        // Otherwise, add it
        return [...prevOptions, { [key]: value }];
      }
    });
  };

  const handleRemoveOption = (index: number) => {
    setOptions((prevOptions) => prevOptions?.filter((_, i) => i !== index));
  };

  const [combinations, setCombinations] = useState<CombinationRow[]>([]);

  // const [combinations, setCombinations] = useState<
  //   {
  //     id: string;
  //     sku: string;
  //     description: string;
  //     attributes: Record<string, string>;
  //     price: number;
  //     quantity?: number;
  //     warehouseData: {
  //       warehouse_id: string;
  //       warehouse_name: string;
  //       zone_id: string;
  //       zone_name: string;
  //       rack_id: string;
  //       rack_name: string;
  //       bin_id: string;
  //       bin_name: string;
  //       on_hand: number;
  //       available: number;
  //       reserved: number;
  //     }[];
  //   }[]
  // >([]);


  useEffect(() => {
    setCombErrors(
      combinations.map(c => c.warehouseData.map(() => ({ zone: false, rack: false, bin: false })))
    );
  }, [combinations]);

  useEffect(() => {
    if (!isEditMode || editIndex === null || !combinations.length) return;

    async function loadLocationOptions() {
      const opts: Record<string, { zones: any[]; racks: any[]; bins: any[] }> = {};

      for (const warehouse of warehouses) {
        opts[warehouse._id] = { zones: [], racks: [], bins: [] };

        try {
          const zoneData = await fetchHierarchy({ warehouse_id: warehouse._id });
          opts[warehouse._id].zones = zoneData.zones || [];
          const warehouseData = (editIndex !== null && editIndex !== undefined)
            ? combinations[editIndex]?.warehouseData?.find(wd => wd.warehouse_id === warehouse._id)
            : undefined;

          if (warehouseData?.zone_id) {
            const rackData = await fetchHierarchy({ zone_id: warehouseData.zone_id });
            opts[warehouse._id].racks = rackData.racks || [];

            if (warehouseData?.rack_id) {
              const binData = await fetchHierarchy({ rack_id: warehouseData.rack_id });
              opts[warehouse._id].bins = binData.bins || [];
            }
          }
        } catch (err) {
          safeConsole.error(err);
        }
      }

      setLocationOptions(opts);
    }

    loadLocationOptions();
  }, [isEditMode, editIndex, warehouses, combinations]);

  const mapCombinationRowToVariantPayload = (comb: CombinationRow, values: FormValues, opts?: { includeId?: boolean }) => {
    const isPerVariant = values.shipping_config === "per_variant";
    const dims = isPerVariant
      ? {
        length: Number((comb as any).length || 0),
        height: Number((comb as any).height || 0),
        width: Number((comb as any).width || 0),
        weight: Number((comb as any).weight || 0),
        weight_type: "kg",
        dimension_unit: "Inches",
        weight_unit: "Pounds",
      }
      : {
        length: Number(values.length || 0),
        height: Number(values.height || 0),
        width: Number(values.width || 0),
        weight: Number(values.weight || 0),
        weight_type: "kg",
        dimension_unit: "Inches",
        weight_unit: "Pounds",
      };

    const selIds = comb.imageIds || [];
    const images = selIds.map((id, i) => ({
      url: id,
      alt_text: uploaded.find(u => u.id === id)?.name || "",
      default: i === 0,
    }));

    return {
      variant_name: `${values.name} - ${Object.values(comb.attributes).join(" - ")}`,
      ...(opts?.includeId && comb.variant_id ? { variant_id: comb.variant_id } : {}), // ✅ only include for PATCH
      sku: comb.sku || "",
      description: values.description || "",
      attributes: comb.attributes || {},
      purchase_price: Number(values.purchase_price || 0),
      retail_price: Number(comb.price || values.retail_price || 0),
      images,
      package_dimensions: dims,
      inventory:
        (comb.warehouseData || [])
          .filter(
            (wd) =>
              wd &&
              wd.warehouse_id &&
              wd.warehouse_name &&
              Number(wd.available) > 0 &&
              wd.zone_id &&
              wd.rack_id &&
              wd.bin_id
          )
          .map((wd) => ({
            warehouse_id: wd.warehouse_id,
            warehouse_name: wd.warehouse_name,
            zone_id: wd.zone_id,
            zone_name: wd.zone_name,
            rack_id: wd.rack_id,
            rack_name: wd.rack_name,
            bin_id: wd.bin_id,
            bin_name: wd.bin_name,
            on_hand: Number(wd.on_hand) || Number(wd.available) || 0,
            available: Number(wd.available) || 0,
            reserved: Number(wd.reserved) || 0,
          })) || [],
      meta_data: { slug: createSlug(`${values.name} - ${Object.values(comb.attributes).join(" - ")}`) },
      customizations: {
        template_name: values.name || "",
        fields:
          customFields?.map((f) => ({
            field_name: f.label,
            field_type: f.type,
            required: f.required,
            value: "",
          })) || [],
      },
    };
  };
  const handleCreateVariantsFromCombinations = async (source?: CombinationRow[]) => {
    try {
      const createdAll: any[] = [];
      if (!token || !signageId) return showError("Not authenticated");
      if (!productId) return showError("Please save draft first.");

      const combos = Array.isArray(source) ? source : combinations;
      if (!Array.isArray(combos) || combos.length === 0) {
        return showError("Please generate combinations first.");
      }

      const bodies = combos.map((comb) => mapCombinationRowToVariantPayload(comb, formik.values));

      for (let i = 0; i < bodies.length; i++) {
        const variantPayload = bodies[i];
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(variantPayload),
            }
          );
          const result = await res.json();
          if (!res.ok || result?.error) {
            const msg = result?.error || result?.message || "Failed to create variant";
            throw new Error(msg);
          }

          const vid =
            result?.variant?.id ||
            result?.variant?._id ||
            result?.id ||
            result?._id ||
            result?.variant_id;

          // after computing `vid`:
          if (vid) {
            // ✅ update the *same* row you posted (by array index)
            setCombinations(prev =>
              prev.map((r, idx) => (idx === i ? { ...r, variant_id: String(vid) } : r))
            );

            // keep a simple normalized "created" object if you want, but we can also rely on variantPayload
            const created =
              Array.isArray(result) ? result[0] :
                (Array.isArray(result?.data) ? result.data[0] : result);

            setCombinationsFetch(prev => [
              ...prev,
              {
                id: String(vid),
                attributes: created?.attributes ?? variantPayload?.attributes ?? {},
                value: created?.variant_name ?? variantPayload?.variant_name ?? "",
                label: String(vid),
                sku: created?.sku ?? variantPayload?.sku ?? "",
              },
            ]);
          }
        } catch (variantError: any) {
          await handleApiError(variantError, {
            module: "inventory",
            action: "Signage_Edit_Variable_Product_create_variant",
            userId: signageId || undefined,
            licenseType: licenseType || undefined,
            userName: signageName || undefined,
            url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants`,
            method: "POST",
            requestBody: variantPayload,
          }, token);
          throw variantError;
        }
      }
      fetchProduct();
      showSuccess("Variants generated successfully.");
    } catch (e: any) {
      safeConsole.error(e);
      showError(e?.message || "Failed to generate variants");
    }
  };

  const handleGenerateAndCreate = async () => {
    // 1) Build combos
    const attributeValues = attributes.reduce<Record<string, string[]>>((acc, attr) => {
      const key = (attr?.name || "").toLowerCase();
      const selectedValues =
        (attr?.values || []).filter((value) => options.some((option) => option[key] === value)) || [];
      if (selectedValues.length > 0) acc[key] = selectedValues;
      return acc;
    }, {});

    const keys = Object.keys(attributeValues);
    if (keys.length === 0) {
      showError("No attributes selected.");
      return;
    }

    const defaultDims = {
      length: Number(formik.values.length) || 0,
      width: Number(formik.values.width) || 0,
      height: Number(formik.values.height) || 0,
      weight: Number(formik.values.weight) || 0,
      dimension_unit: "Inches",
      weight_unit: "Pounds",
    };

    const generateCombinations = (
      remainingKeys: string[],
      currentCombination: Record<string, string>
    ): CombinationRow[] => {
      if (remainingKeys.length === 0) {
        const row: CombinationRow = {
          variant_name: "",
          sku: "",
          description: "",
          // quantity:"",
          attributes: currentCombination,
          price: 0,
          ...defaultDims,
          warehouseData: (warehouses || []).map((w) => ({
            warehouse_id: (w as any)._id,
            warehouse_name: (w as any).label,
            zone_id: "",
            zone_name: "",
            rack_id: "",
            rack_name: "",
            bin_id: "",
            bin_name: "",
            on_hand: 0,
            available: 0,
            reserved: 0,
          })),
        };
        return [row];
      }

      const [key, ...restKeys] = remainingKeys;
      const values = attributeValues[key] || [];
      let result: CombinationRow[] = [];

      for (const value of values) {
        result = result.concat(
          generateCombinations(restKeys, { ...currentCombination, [key]: value })
        );
      }
      fetchProduct();
      return result;
    };

    const allCombinations: CombinationRow[] = generateCombinations(keys, {});
    const withSkus: CombinationRow[] = allCombinations.map((comb, index) => ({
      ...comb,
      sku: formik.values.sku ? `${formik.values.sku}-${index + 1}` : "",
    }));

    // 2) Update UI immediately
    setCombinations(withSkus);
    showSuccess("Combinations generated successfully!");

    // 3) Create variants using the *local* array to avoid the async state race
    await handleCreateVariantsFromCombinations(withSkus);
  };


  const handleSaveEditedCombination = async () => {
    if (editIndex === null) return;
    const rowUI = combinations[editIndex] as any;
    // safeConsole.log("Saving combination:", rowUI);
    try {
      if (!token || !signageId) return showError("Not authenticated");
      if (!productId) return showError("Missing product");
      if (!rowUI?.id) return showError("This combination is not linked to a variant yet. Generate variants first.");

      const payload = mapCombinationRowToVariantPayload(rowUI, formik.values);
      // NOTE: server identifies which variant via path parameter; usually better to omit id in body
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${rowUI.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (!res.ok || result?.error) {
        const msg = result?.error || result?.message || "Failed to update variant";
        throw new Error(msg);
      }



      showSuccess("Combination saved.");
      setIsEditMode(false);
      setEditIndex(null);

      // optional: refresh local values from server or keep your local state
      // await loadVariants(productId);
    } catch (e: any) {
      await handleApiError(e, {
        module: "inventory",
        action: "Signage_Edit_Variable_Product_update_variant",
        userId: signageId || undefined,
        licenseType: licenseType || undefined,
        userName: signageName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${rowUI.id}`,
        method: "PATCH",
        requestBody: mapCombinationRowToVariantPayload(rowUI, formik.values),
      }, token);
      safeConsole.error(e);
      showError(e?.message || "Failed to save combination");
    }
  };
  const is24Hex = (s?: string) => typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);

  async function deleteVariantFromServer(variantId: string) {
    try {
      const url = `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${variantId}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to delete variant");
      }
    } catch (error: any) {
      await handleApiError(error, {
        module: "inventory",
        action: "Signage_Edit_Variable_Product_delete_variant",
        userId: signageId || undefined,
        licenseType: licenseType || undefined,
        userName: signageName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${variantId}`,
        method: "DELETE",
      }, token);
      throw error;
    }
  }


  useEffect(() => {
    async function loadOptions() {
      if (!isEditMode || editIndex === null) return;
      const opts: Record<string, { zones: any[]; racks: any[]; bins: any[] }> = {};

      await Promise.all(
        warehouses.map(async (warehouse, wIdx) => {
          opts[warehouse._id] = { zones: [], racks: [], bins: [] };
          try {
            const zoneRes = await fetchHierarchy({ warehouse_id: warehouse._id });
            opts[warehouse._id].zones = zoneRes.zones || [];

            const warehouseData = combinations[editIndex]?.warehouseData || [];
            const whData = warehouseData[wIdx] || {};
            if (whData.zone_id) {
              const rackRes = await fetchHierarchy({ zone_id: whData.zone_id });
              opts[warehouse._id].racks = rackRes.racks || [];
              if (whData.rack_id) {
                const binRes = await fetchHierarchy({ rack_id: whData.rack_id });
                opts[warehouse._id].bins = binRes.bins || [];
              }
            }
          } catch { }
        })
      );

      setLocationOptions(opts);
    }
    loadOptions();
    // eslint-disable-next-line
  }, [isEditMode, editIndex, warehouses, combinations]);

  // helper (put near the top of the component)
  const safeParseJson = (s: any) => {
    if (typeof s !== "string") return s ?? {};
    try { return JSON.parse(s); } catch { return {}; }
  };


  useEffect(() => {
    if (variants && variants.length > 0 && warehouses.length > 0) {
      const existingCombinations: CombinationRow[] = variants.map((variant: any) => {
        // Build a quick lookup map for inventory
        const invMap = new Map<string, any>();
        (variant.inventory || []).forEach((inv: any) => invMap.set(inv.warehouse_id, inv));

        // Always include every warehouse
        const warehouseData = warehouses.map((w: any) => {
          const existing = invMap.get(w._id) || {};
          return {
            warehouse_id: w._id,
            warehouse_name: w.label,
            zone_id: existing.zone_id || "",
            zone_name: existing.zone_name || "",
            rack_id: existing.rack_id || "",
            rack_name: existing.rack_name || "",
            bin_id: existing.bin_id || "",
            bin_name: existing.bin_name || "",
            on_hand: Number(existing.on_hand ?? existing.available ?? 0),
            available: Number(existing.available ?? 0),
            reserved: Number(existing.reserved ?? 0),
          };
        });

        // ⬇️ ensure the 4 required dimension fields exist
        const dims = {
          length: Number(variant?.package_dimensions?.length ?? 0),
          width: Number(variant?.package_dimensions?.width ?? 0),
          height: Number(variant?.package_dimensions?.height ?? 0),
          weight: Number(variant?.package_dimensions?.weight ?? 0),
          dimension_unit: "Inches",
          weight_unit: "Pounds",
        };

        return {
          id: variant.variant_id,
          variant_name: variant.variant_name,
          sku: variant.sku,
          description: variant.description ?? "",
          attributes: safeParseJson(variant.attributes) || {},
          price: Number(variant.retail_price ?? 0),
          imageIds: (variant.images || []).map((im: any) => im.url),
          quantity: warehouseData.reduce(
            (sum: number, w: any) => sum + (Number(w.available) || 0),
            0
          ),
          warehouseData,
          ...dims, // ✅ add required fields
        } as CombinationRow;
      });

      setCombinations(existingCombinations);
    }
  }, [variants, warehouses]);



  const createSlug = (input: string): string => {
    return input
      .toLowerCase() // Convert to lowercase
      .trim() // Remove leading and trailing whitespace
      .replace(/[^a-z0-9\s-]/g, "") // Remove invalid characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-"); // Replace multiple hyphens with a single one
  };






  const generateProductNames = (
    productName: string,
    options: { [key: string]: string }[]
  ) => {
    return options?.map((option) => {
      const formattedAttributes = Object.values(option)
        .map((value) => value.trim()) // Trim whitespace
        .join(" - "); // Combine attributes with " - "
      return `${productName} - ${formattedAttributes}`;
    });
  };


  // Build variants from current combinations array
  const generateProductVariants = async (combs: any[]) => {
    const result = await Promise.all(
      (combs || []).map(async (combination, index) => {
        // pick dimensions based on shipping configuration
        const dims =
          formik.values.shipping_config === "per_variant"
            ? {
              length: Number(combination?.length ?? 0),
              height: Number(combination?.height ?? 0),
              width: Number(combination?.width ?? 0),
              weight: Number(combination?.weight ?? 0),
              weight_type: "kg",
              dimension_unit: "Inches",
              weight_unit: "Pounds",
            }
            : {
              length: Number(formik.values.length ?? 0),
              height: Number(formik.values.height ?? 0),
              width: Number(formik.values.width ?? 0),
              weight: Number(formik.values.weight ?? 0),
              weight_type: "kg",
              dimension_unit: "Inches",
              weight_unit: "Pounds",
            };

        const variantName = `${formik.values.name} - ${Object.values(
          combination?.attributes || {}
        ).join(" - ")}`;

        const selIds = (combination.imageIds || []);
        const images = selIds.map((id: any, i: number) => ({
          url: id,
          alt_text: uploaded.find(u => u.id === id)?.name || "",
          default: i === 0,
        }));
        return {
          ...(combination?.variant_id || combination?.id
            ? { variant_id: String(combination.variant_id ?? combination.id) }
            : {}),
          variant_name: variantName,
          sku:
            combination?.sku ||
            (formik.values.sku ? `${formik.values.sku}-${index + 1}` : ""),
          description: combination?.description || "",
          attributes: combination?.attributes || {},
          purchase_price: Number(formik.values.purchase_price ?? 0),
          retail_price: Number(combination?.price ?? 0),
          images,
          package_dimensions: dims,
          threshold_qty: 0,

          inventory: (combination?.warehouseData || [])
            .filter(
              (w: any) =>
                w &&
                w.warehouse_id &&
                w.warehouse_name &&
                Number(w.available) > 0
            )
            .map((w: any) => ({
              warehouse_id: w.warehouse_id,
              warehouse_name: w.warehouse_name,
              zone_id: w.zone_id || "",
              zone_name: w.zone_name || "",
              rack_id: w.rack_id || "",
              rack_name: w.rack_name || "",
              bin_id: w.bin_id || "",
              bin_name: w.bin_name || "",
              on_hand: Number(w.on_hand ?? w.available ?? 0),
              available: Number(w.available ?? 0),
              reserved: Number(w.reserved ?? 0),
            })),

          meta_data: { slug: createSlug(variantName) },

          customizations: {
            template_name: formik.values.name,
            fields:
              (customFields || []).map((f: any) => ({
                field_name: f.label,
                field_type: f.type,
                required: f.required,
                value: "",
              })) || [],
          },

          is_rotational: formik.values.is_rotational === "yes",
        };
      }));
    return result;
  };


  const reverseProductVariants = (variants: any[]) => {
    if (variants.length === 0) {
      return {
        productNames: [],
        values: {},
        exoptions: [],
      };
    }

    // Extract productNames
    const productNames = variants?.map((variant) => variant.variant_name);

    // Extract common values from the first variant
    const firstVariant = variants[0];
    const values: FormValues = {
      name: "",
      product_type: "variable", // Assuming product type is "variable"
      sku: firstVariant.sku.split("-")[0], // Extract SKU base
      description: "", // Placeholder as it's not in variants
      brand: "", // Placeholder as it's not in variants
      brand_id: "",
      category: "", // Placeholder as it's not in variants
      category_id: "", // Placeholder as it's not in variants
      item_condition: "", // Placeholder as it's not in variants
      condition_note: "", // Placeholder as it's not in variants
      purchase_price: firstVariant?.purchase_price,
      retail_price: firstVariant.retail_price,
      length: firstVariant?.package_dimensions?.length,
      height: firstVariant?.package_dimensions?.height,
      width: firstVariant?.package_dimensions?.width,
      weight: firstVariant?.package_dimensions?.weight,
      threshold_qty: "", // Placeholder as it's not in variants
      attribute: "", // Placeholder as it's not in variants
      parameter: "", // Placeholder as it's not in variants
      images: null,
      statuss: "", // Placeholder as it's not in variants
      is_rotational: "", // Placeholder as it's not in variants
      is_production: "", // Placeholder as it's not in variants
      warehouseId: firstVariant?.inventory[0]?.warehouse_id || "",
      on_hand: String(firstVariant?.inventory[0]?.on_hand || 0),
      available: String(firstVariant?.inventory[0]?.available || 0),
      reserved: String(firstVariant?.inventory[0]?.reserved || 0),
      is_taxable: productobj?.product?.is_taxable || false,
      taxRule: productobj?.product?.tax_rule || null,
      taxGroup: productobj?.product?.tax_group
        ? { ...productobj.product.tax_group, status: String(productobj.product.tax_group.status) }
        : null,
      tax_rule_id: "",
      shipping_config: "per_variant",
    };

    // Extract options from each variant
    const exoptions = variants?.map((variant) => variant.attributes || {});

    return { productNames, values, exoptions };
  };

  const { productNames, values, exoptions } = reverseProductVariants(variants);
  
  // ✅ Brand options from hook (with product's current brand included if not in list)
  const brandOptions = useMemo(() => {
    const brandOptions = brandOptionsFromHook;
    const productBrandId = (productobj?.product as any)?.brand_id || ""; // ✅ Only use brand_id, not brand name
    const productBrandName = productobj?.product?.brand || "";

    // ✅ Only add product's brand if we have a valid ID (not a name)
    if (productBrandId && productBrandName && /^[a-f0-9]{24}$/i.test(productBrandId)) {
      const brandExists = brandOptions.some(opt => opt.value === productBrandId);
      if (!brandExists) {
        return [
          { value: productBrandId, label: productBrandName },
          ...brandOptions
        ];
      }
    }

    return brandOptions;
  }, [brandOptionsFromHook, productobj]);

  // ✅ Category options from hook (filtered by selected brand, with product's current category included if not in list)
  const categoryOptions = useMemo(() => {
    // ✅ Get categories from hook (only if brand is selected)
    const hookCategories = selectedBrand ? categoryOptionsFromHook : [];
    
    // ✅ Always include product's category if it exists (for edit mode initial load)
    const productCategoryId = (productobj?.product as any)?.category_id || ""; // ✅ Only use category_id, not category name
    const productCategoryName = productobj?.product?.category || "";
    
    // ✅ Only add product's category if we have a valid ID (not a name)
    if (productCategoryId && productCategoryName && /^[a-f0-9]{24}$/i.test(productCategoryId)) {
      const categoryExists = hookCategories.some(opt => opt.value === productCategoryId);
      if (!categoryExists) {
        // Add product's category to the list
        return [
          { value: productCategoryId, label: productCategoryName },
          ...hookCategories
        ];
      }
    }

    return hookCategories;
  }, [categoryOptionsFromHook, selectedBrand, productobj]);
  
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      name: productobj?.product?.name || "",
      product_type: "variable",
      sku: productobj?.product?.sku || "",
      description: productobj?.product?.description || "",
      brand: (productobj?.product as any)?.brand_id || "",  // ✅ Store ID for dropdown (dropdown uses ID as value)
      brand_id: (productobj?.product as any)?.brand_id || "",  // ✅ Store brand ID
      category: (productobj?.product as any)?.category_id || "",  // ✅ Store ID for dropdown (dropdown uses ID as value)
      category_id: (productobj?.product as any)?.category_id || "",  // ✅ Store category ID
      item_condition: productobj?.product?.item_condition || "",
      condition_note: productobj?.product?.condition_note || "",
      purchase_price: String(productobj?.product?.purchase_price || 0),
      retail_price: String(productobj?.product?.retail_price || 0),
      length: String(productobj?.product?.package_dimensions?.length || 0),
      height: String(productobj?.product?.package_dimensions?.height || 0),
      width: String(productobj?.product?.package_dimensions?.width || 0),
      weight: String(productobj?.product?.package_dimensions?.weight || 0),
      threshold_qty: String(productobj?.product?.threshold_qty || 0),
      attribute: productobj?.product?.name || "",
      parameter: "",
      images: null,
      statuss: productobj?.product?.status || "Active",
      is_rotational: productobj?.product?.is_rotational ? "yes" : "no",
      is_production: productobj?.product?.is_production ? "yes" : "no",
      warehouseId: productobj?.product?.variants?.[0]?.inventory?.[0]?.warehouse_id || "",
      on_hand: String(productobj?.product?.variants?.[0]?.inventory?.[0]?.on_hand || 0),
      available: String(productobj?.product?.variants?.[0]?.inventory?.[0]?.available || 0),
      reserved: String(productobj?.product?.variants?.[0]?.inventory?.[0]?.reserved || 0),
      is_taxable: productobj?.product?.is_taxable || false,
      taxRule: productobj?.product?.tax_rule || null,
      taxGroup: productobj?.product?.tax_group
        ? { ...productobj.product.tax_group, status: String(productobj.product.tax_group.status) }
        : null,
      tax_rule_id: productobj?.product?.tax_rule?._id || "",
      shipping_config: toConfigString(productobj?.product?.shipping_config),
    },
    validationSchema: Yup.object({
      name: nameValidation,
      sku: Yup.string().required("SKU is required"),
      retail_price: Yup.number().required("Retail price is required"),
      // is_rotational: Yup.string().required("Is Rotational is required"),
      is_production: Yup.string().required("Is Production is required"),
      // statuss: statusValidation,
    }),

    onSubmit: async (values) => {
      const isGlobalShipping = values.shipping_config === "global";
      setFormSubmitting(true);

      // ✅ STEP 1: Upload all pending files first (if user didn't click "Save Images" yet)
      let allUploadedImages = [...uploaded]; // Start with already uploaded images
      
      try {
        if (pendingFiles.length > 0) {
          try {
            const filesToUpload = pendingFiles.map(p => p.file);
            const uploadedIds = await uploadFilesAndGetIds(filesToUpload, token || null);

            // Add newly uploaded files to the list
            const newlyUploaded: UploadedImage[] = uploadedIds.map((id, i) => ({
              id,
              name: filesToUpload[i].name,
              size: filesToUpload[i].size,
            }));

            // Merge with existing uploaded images
            allUploadedImages = [...uploaded, ...newlyUploaded];
            
            // Update state
            setUploaded(allUploadedImages);

            // Clean up preview URLs
            pendingFiles.forEach(p => URL.revokeObjectURL(p.previewUrl));
            setPendingFiles([]);

            showSuccess(`${uploadedIds.length} image(s) uploaded successfully!`);
          } catch (uploadErr: any) {
            safeConsole.error("Image upload failed:", uploadErr);
            showError(uploadErr?.message || "Failed to upload images. Please try again.");
            setFormSubmitting(false);
            return; // Don't proceed if upload fails
          }
        }

        // ✅ STEP 2: Delete marked images
        if (pendingDeleteIds.length > 0) {
          try {
            await hardDeletePresignedFiles(pendingDeleteIds, token || null);
            // Remove deleted images from the list
            allUploadedImages = allUploadedImages.filter(u => !pendingDeleteIds.includes(u.id));
            setUploaded(allUploadedImages);
            setPendingDeleteIds([]); // clear
          } catch (delErr: any) {
            safeConsole.error("Image hard delete (batch) failed:", delErr);
            // Don't block success — just inform
            showError(delErr?.message || "Some removed images could not be deleted.");
          }
        }
      } catch (err: any) {
        safeConsole.error("Image processing error:", err);
        showError("Failed to process images.");
        setFormSubmitting(false);
        return;
      }

      // ✅ STEP 3: Build productImages from all uploaded images (including newly uploaded ones)
      const productImages = allUploadedImages.map((u, i) => ({
        url: u.id,
        alt_text: u.name,
        default: i === 0,
      }));

      let updatePayload: any = undefined;
      try {
        const filteredCombinations = combinations.map((comb) => ({
          ...comb,
          warehouseData: comb.warehouseData.filter((w) => Number(w.available) > 0),
        }));


        const hasAnyQuantity = combinations.some(comb =>
          comb.warehouseData.some(row => row.available > 0)
        );

        if (!hasAnyQuantity) {
          toast.error("Enter quantity for at least one warehouse.");
          return;
        }

        // Pass filtered combinations to generateProductVariants
        const productVariants = await generateProductVariants(filteredCombinations);
        const selectedTaxRule = taxRules?.find(
          (rule) => rule._id === values.tax_rule_id
        );

        // API call with filtered productVariants
        updatePayload = {
          name: values.name,
          product_type: values.product_type,
          sku: values.sku,
          description: values.description,
          brand: (() => {
            // ✅ Send brand NAME (not ID)
            const brandValue = values.brand || selectedBrand || "";
            // Check if brandValue is already an ID (24-hex string)
            const isId = /^[a-f0-9]{24}$/i.test(brandValue);
            const brandOpt = isId 
              ? brandOptions.find(b => b.value === brandValue)
              : brandOptions.find(b => b.label === brandValue || b.value === brandValue);
            return brandOpt?.label || "";
          })(),
          brand_id: (() => {
            // ✅ Send brand ID (not name) - always ensure we return an ID
            const brandValue = values.brand || selectedBrand || "";
            // Check if brandValue is already an ID (24-hex string)
            const isId = /^[a-f0-9]{24}$/i.test(brandValue);
            if (isId) {
              // It's already an ID, use it directly
              return brandValue;
            }
            // It's a name, find the ID from options
            const brandOpt = brandOptions.find(b => b.label === brandValue || b.value === brandValue);
            return brandOpt?.value || "";
          })(),
          category: (() => {
            // ✅ Send category NAME (not ID)
            const categoryValue = values.category || selectedCategories[0] || "";
            // Check if categoryValue is already an ID (24-hex string)
            const isId = /^[a-f0-9]{24}$/i.test(categoryValue);
            const catOpt = isId 
              ? categoryOptions.find(c => c.value === categoryValue)
              : categoryOptions.find(c => c.label === categoryValue || c.value === categoryValue);
            return catOpt?.label || "";
          })(),
          category_id: (() => {
            // ✅ Send category ID (not name) - always ensure we return an ID
            const categoryValue = values.category || selectedCategories[0] || "";
            // Check if categoryValue is already an ID (24-hex string)
            const isId = /^[a-f0-9]{24}$/i.test(categoryValue);
            if (isId) {
              // It's already an ID, use it directly
              return categoryValue;
            }
            // It's a name, find the ID from options
            const catOpt = categoryOptions.find(c => c.label === categoryValue || c.value === categoryValue);
            return catOpt?.value || "";
          })(),
          item_condition: values.item_condition,
          condition_note: values.condition_note,
          purchase_price: parseFloat(values.purchase_price as string),
          retail_price: parseFloat(values.retail_price as string),
          ...(values.shipping_config === "global"
            ? {
              package_dimensions: {
                length: Number(values.length || 0),
                height: Number(values.height || 0),
                width: Number(values.width || 0),
                weight: Number(values.weight || 0),
                dimension_unit: "Inches",
                weight_unit: "Pounds",
              },
            }
            : {}),
          // package_dimensions: {
          //   length: parseFloat(values.length as string),
          //   height: parseFloat(values.height as string),
          //   width: parseFloat(values.width as string),
          //   weight: parseFloat(values.weight as string),
          // },
          shipping_config: isGlobalShipping,
          min_qty: 1,
          threshold_qty: parseFloat(values.threshold_qty as string),
          variants: productVariants, // <-- Use filtered variants here
          images: productImages,
          status: values.statuss,
          is_rotational: values.is_rotational === "yes",
          is_production: values.is_production === "yes",
          is_taxable: values.is_taxable,
          tax_rule: values.is_taxable && selectedTaxRule ? selectedTaxRule : null,
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updatePayload),
          }
        );

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Product updated successfully!");
        safeNavigate(`${baseRoute}/products`);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Edit_Variable_Product_update_product",
          userId: signageId || undefined,
          licenseType: licenseType || undefined,
          userName: signageName || undefined,
          url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}`,
          method: "PATCH",
          requestBody: updatePayload,
        }, token);
        safeConsole.error(err instanceof Error ? err.message : "Unknown error");
        toast.error("Failed to update Product.");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // ✅ This effect is now handled by the Product→Hook sync logic above
  // Removed to prevent conflicts with the hook-based sync

  // ✅ Refs to prevent circular updates and track previous values
  const isSyncingFromFormik = useRef(false);
  const isSyncingFromHook = useRef(false);
  const prevProductBrandId = useRef<string>("");
  const prevProductCategoryId = useRef<string>("");
  const prevFormikBrand = useRef<string>("");
  const prevFormikCategory = useRef<string>("");
  const prevSelectedBrand = useRef<string>("");
  const prevSelectedCategory = useRef<string>("");

  // ✅ Sync product's brand/category to hook when product loads (Edit mode) - Only once
  useEffect(() => {
    if (!productobj?.product) return;
    
    const productBrandId = (productobj?.product as any)?.brand_id || "";
    const productCategoryId = (productobj?.product as any)?.category_id || ""; // ✅ Only use category_id, not category name
    
    // Only set brand if it's different and hasn't been set yet
    if (productBrandId && productBrandId !== prevProductBrandId.current && productBrandId !== selectedBrand) {
      prevProductBrandId.current = productBrandId;
      // safeConsole.log("🔄 [Product→Hook] Setting brand from product:", productBrandId);
      isSyncingFromFormik.current = true; // Prevent formik sync from interfering
      setSelectedBrand(productBrandId);
      // Also set formik value directly to ensure it's in the UI
      formik.setFieldValue("brand", productBrandId, false);
      // Clear categories when brand changes - they will be reloaded by the hook
      setSelectedCategories([]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [productobj?.product, selectedBrand, setSelectedBrand, setSelectedCategories, formik]);

  // ✅ Sync category AFTER brand is set and categories are loaded
  useEffect(() => {
    if (!productobj?.product) return;
    
    const productBrandId = (productobj?.product as any)?.brand_id || "";
    const productCategoryId = (productobj?.product as any)?.category_id || ""; // ✅ Only use category_id, not category name
    
    // Only sync category if:
    // 1. Brand is already set (matches product's brand)
    // 2. Categories are available (not loading AND categories exist)
    // 3. Category ID exists
    // 4. Category hasn't been set yet
    // Check if categories are available (either from hook or in computed options)
    const hasCategories = !loadingCategories && (categoryOptionsFromHook.length > 0 || categoryOptions.length > 0);
    
    if (
      productBrandId && 
      productCategoryId && 
      selectedBrand === productBrandId && 
      hasCategories &&
      productCategoryId !== prevProductCategoryId.current
    ) {
      const currentFirst = selectedCategories[0] || "";
      if (productCategoryId !== currentFirst) {
        prevProductCategoryId.current = productCategoryId;
        // safeConsole.log("🔄 [Product→Hook] Setting category from product:", productCategoryId);
        isSyncingFromFormik.current = true; // Prevent formik sync from interfering
        setSelectedCategories([productCategoryId]);
        // Also set formik value directly to ensure it's in the UI
        formik.setFieldValue("category", productCategoryId, false);
        setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
      }
    }
  }, [productobj?.product, selectedBrand, selectedCategories, loadingCategories, categoryOptionsFromHook.length, categoryOptions.length, setSelectedCategories, formik]);

  // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }
    const brandValue = formik.values.brand || "";
    if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
      prevFormikBrand.current = brandValue;
      prevFormikCategory.current = "";
      isSyncingFromFormik.current = true;
      setSelectedBrand(brandValue);
      setSelectedCategories([]);
      formik.setFieldValue("category", "", false);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.brand, selectedBrand, setSelectedBrand, setSelectedCategories, formik]);

  // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }
    const categoryValue = formik.values.category || "";
    const currentFirst = selectedCategories[0] || "";
    if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
      prevFormikCategory.current = categoryValue;
      isSyncingFromFormik.current = true;
      setSelectedCategories([categoryValue]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.category, selectedCategories, setSelectedCategories]);

  // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) return;
    if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
      prevSelectedBrand.current = selectedBrand;
      isSyncingFromHook.current = true;
      formik.setFieldValue("brand", selectedBrand, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    }
  }, [selectedBrand, formik.values.brand]);

  // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) return;
    const firstCategory = selectedCategories[0] || "";
    if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
      prevSelectedCategory.current = firstCategory;
      isSyncingFromHook.current = true;
      formik.setFieldValue("category", firstCategory, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    } else if (!firstCategory && formik.values.category) {
      if (prevSelectedCategory.current !== "") {
        prevSelectedCategory.current = "";
        isSyncingFromHook.current = true;
        formik.setFieldValue("category", "", false);
        setTimeout(() => { isSyncingFromHook.current = false; }, 0);
      }
    }
  }, [selectedCategories, formik.values.category]);

  const selectedBrandOption = brandOptions.find(b => b.value === (formik.values.brand || selectedBrand));
  const selectedCategoryOption = categoryOptions.find(c => c.value === (formik.values.category || selectedCategories[0]));

  const PAGE_SIZE = 10;
  const effectiveUserId = signageId || userId || "";
  type SelectOptionType = { value: string; label: string };
  type AdditionalType = { page?: number };

  const loadBrandOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const currentPage = additional?.page ?? 1;
      try {
        const resp = await fetchBrandsByUserSelect2(effectiveUserId, token || "", {
          q: (inputValue || "").trim(),
          page: currentPage,
          limit: PAGE_SIZE,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Brand"),
        }));
        const hasMore = !!resp.pagination?.more;
        return { options, hasMore, additional: { page: currentPage + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
    },
    [effectiveUserId, token]
  );

  const loadCategoryOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      if (!selectedBrand) return { options: [], hasMore: false, additional: { page: 1 } };
      const currentPage = additional?.page ?? 1;
      try {
        const resp = await fetchCategoriesByUserSelect2(effectiveUserId, token || "", {
          q: (inputValue || "").trim(),
          page: currentPage,
          limit: PAGE_SIZE,
        });
        let options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Category"),
        }));
        if (brandCategoryIds?.length) {
          const idSet = new Set(brandCategoryIds);
          options = options.filter((o) => idSet.has(o.value));
        }
        const hasMore = !!resp.pagination?.more;
        return { options, hasMore, additional: { page: currentPage + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
    },
    [effectiveUserId, token, selectedBrand, brandCategoryIds]
  );

  const mobileColumns: ColumnDef<CombRow, any>[] = [
    // SNO
    columnHelper.display({
      id: "sno",
      header: () => "SNO",
      cell: info => info.row.index + 1,
    }),

    // Combination (attributes => "key: value, key2: value2")
    columnHelper.accessor(row =>
      Object.entries(row.attributes).map(([k, v]) => `${k}: ${v}`).join(", ")
      , {
        id: "combination",
        header: () => "Combination",
        cell: info => <div className="text-wrap">{info.getValue()}</div>,
      }),

    // Price (editable)
    columnHelper.display({
      id: "price",
      header: () => "Price",
      cell: info => {
        const idx = info.row.index;
        const value = combinations[idx]?.price ?? 0;
        return (
          <input
            type="number"
            className="form-control"
            placeholder="0"
            value={value === 0 ? "" : value}
            onChange={(e) => {
              const price = parseFloat(e.target.value) || 0;
              setCombinations(prev => prev.map((c, i) => (i === idx ? { ...c, price } : c)));
            }}
          />
        );
      },
    }),

    // Actions
    columnHelper.display({
      id: "actions",
      header: () => "Action",
      cell: (info) => {
        const idx = info.row.index;
        const row = info.row.original as CombinationRow;

        // Prefer real variant id; only treat as "saved" if it's 24-hex
        const variantId = String(row?.variant_id ?? row?.id ?? "");
        const hasServerId = is24Hex(variantId);
        // delKey drives spinner/disabled; use server id if present, else a tmp key per row
        const delKey = hasServerId ? variantId : `tmp-${idx}`;

        return (
          <div className="d-flex gap-2">
            <EditIconButton
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setIsEditMode(true);
                setEditIndex(idx);
                // Initialize temp imageIds with current values
                setTempImageIds([...(combinations[idx]?.imageIds || [])]);
              }}
              title="Edit"
            />

            <DeleteIconButton
              type="button"
              className="btn btn-link text-danger p-0"
              title="Remove"
              disabled={deletingIds.has(delKey)}
              onClick={async () => {
                setDeletingIds(prev => new Set(prev).add(delKey));
                try {
                  if (hasServerId) {
                    await deleteVariantFromServer(variantId);   // ✅ real server delete
                  }
                  // remove from UI either way
                  setCombinations(prev => prev.filter((_, i) => i !== idx));
                  showSuccess?.("Variant deleted.");
                  // optional hard re-sync:
                  // await fetchProduct();
                } catch (err: any) {
                  safeConsole.error(err);
                  showError?.(err?.message || "Failed to delete variant.");
                } finally {
                  setDeletingIds(prev => {
                    const n = new Set(prev);
                    n.delete(delKey);
                    return n;
                  });
                }
              }}
            >
              {deletingIds.has(delKey) ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <i className="bi bi-trash" />
              )}
            </DeleteIconButton>
          </div>
        );
      },
    }),

  ];

  const mobileData = useMemo<CombRow[]>(
    () => combinations as CombRow[],
    [combinations]
  );

  const mobileTable = useReactTable({
    data: mobileData,
    columns: mobileColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isProductDetailsFilled =
    !!formik.values.name &&
    !!formik.values.sku &&
    !!formik.values.retail_price &&
    formik.values.is_production !== "" &&
    !!formik.values.statuss;
  //   if (!isLoaded) return <div>Loading...</div>;

  const resetGlobalDimsToZero = () => {
    formik.setFieldValue("length", 0);
    formik.setFieldValue("width", 0);
    formik.setFieldValue("height", 0);
    formik.setFieldValue("weight", 0);
  };

  const resetAllCombinationDimsToZero = () => {
    // Only matters if you’re using per-variant dims
    setCombinations(prev =>
      (prev || []).map(c => ({
        ...c,
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
      }))
    );
  };

  //   useEffect(() => {
  //   if (!productobj?.product) return;

  //   const isGlobal = productobj.product.shipping_config;
  //   const pd = productobj.product.package_dimensions || {};

  //   // Set radio + global dims correctly
  //   formik.setValues(v => ({
  //     ...v,
  //     shipping_config: isGlobal ? "global" : "per_variant",
  //     // Only keep global dims if using global shipping; otherwise zero them so the UI starts clean
  //     length: String(isGlobal ? (pd.length ?? 0) : 0),
  //     width:  String(isGlobal ? (pd.width  ?? 0) : 0),
  //     height: String(isGlobal ? (pd.height ?? 0) : 0),
  //     weight: String(isGlobal ? (pd.weight ?? 0) : 0),
  //   }));
  // }, [productobj]); 
  // make sure every item has a string id and attributes object
  const combosForDiscount = (combinations || []).map((c: any) => {
    const safeId = String(c.id ?? c.variant_id ?? c.sku ?? "");
    return {
      id: safeId,                                      // required
      variant_id: safeId,                              // convenient duplicate
      variant_name: c.variant_name ?? "",
      sku: c.sku ?? "",
      attributes: (c.attributes && typeof c.attributes === "object") ? c.attributes : {},
    };
  }).filter(c => !!c.id);


  return (
    <div className="container-xxl py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Edit Variable Product</h4>
      </div>

      {/* Tabs */}
      <ul className="nav  gap-2 flex-wrap mb-4 rc-tabs" role="tablist">
        <li className="nav-item">
          <a className="activetabs active" data-bs-toggle="tab" href="#product-details" role="tab">
            Product Details
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`activetabs ${!isProductDetailsFilled ? "disabled" : ""}`}
            data-bs-toggle="tab"
            href="#images"
            role="tab"
            onClick={(e) => { if (!isProductDetailsFilled) e.preventDefault(); }}
          >
            Images
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`activetabs ${!isProductDetailsFilled ? "disabled" : ""}`}
            data-bs-toggle="tab"
            href="#combinations"
            role="tab"
            onClick={(e) => { if (!isProductDetailsFilled) e.preventDefault(); }}
          >
            Combinations
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`activetabs ${!isProductDetailsFilled ? "disabled" : ""}`}
            data-bs-toggle="tab"
            href="#volume-discount"
            role="tab"
            onClick={(e) => { if (!isProductDetailsFilled) e.preventDefault(); }}
          >
            Volume Discount
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`activetabs ${!isProductDetailsFilled ? "disabled" : ""}`}
            data-bs-toggle="tab"
            href="#customization"
            role="tab"
            onClick={(e) => { if (!isProductDetailsFilled) e.preventDefault(); }}
          >
            Customization
          </a>
        </li>
        <li className="nav-item">
          <a
            className={`activetabs ${!isProductDetailsFilled ? "disabled" : ""}`}
            data-bs-toggle="tab"
            href="#design"
            role="tab"
            onClick={(e) => {
              if (!isProductDetailsFilled) {
                e.preventDefault();
                return;
              }
              // Call fetchProduct when Design tab is clicked to get latest variants
              if (productId) {
                fetchProduct();
              }
            }}
          >
            Design
          </a>
        </li>
      </ul>

      <form onSubmit={formik.handleSubmit}>
        <div className="tab-content">
          {/* ================= PRODUCT DETAILS ================= */}
          <div className="tab-pane fade show active" id="product-details" role="tabpanel">
            <div className="box-shadow-datatables shadow-border-1 p-5">
              <div className="row g-4">
                {/* LEFT */}
                <div className="col-lg-6">
                  <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                    <div {...iconLabelProps(16)}>
                      <Package {...withIconStyle("#4a5565", 19)} />
                      <span className="header-color-product"> Product Information</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <UIInputFF
                      formik={formik}
                      name="name"
                      label="Product Name"
                      required
                      placeholder="Enter product name"
                    />
                  </div>

                  <div className="mb-3 col-md-8">
                    <label className="form-label">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <AsyncPaginate
                      classNamePrefix="rs"
                      placeholder="Search or select brand..."
                      value={
                        selectedBrandOption
                          ? { value: selectedBrandOption.value, label: selectedBrandOption.label }
                          : null
                      }
                      loadOptions={loadBrandOptions}
                      additional={{ page: 1 }}
                      onChange={(selected) => {
                        if (selected) {
                          formik.setFieldValue("brand", selected.value, true);
                          setSelectedBrand(selected.value);
                          setSelectedCategories([]);
                        } else {
                          formik.setFieldValue("brand", "", true);
                          setSelectedBrand("");
                          setSelectedCategories([]);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("brand", true)}
                      isClearable
                      className={formik.touched?.brand && formik.errors?.brand ? "is-invalid" : ""}
                      styles={{
                        control: (base: any) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: formik.touched?.brand && formik.errors?.brand ? "#dc3545" : base.borderColor,
                        }),
                      }}
                    />
                    {formik.touched?.brand && formik.errors?.brand && (
                      <div className="invalid-feedback d-block">{formik.errors.brand}</div>
                    )}
                  </div>

                  <div className="mb-3 col-md-8">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <AsyncPaginate
                      classNamePrefix="rs"
                      placeholder={selectedBrand ? "Search or select category..." : "Select brand first"}
                      isDisabled={!selectedBrand}
                      value={
                        selectedCategoryOption
                          ? { value: selectedCategoryOption.value, label: selectedCategoryOption.label }
                          : null
                      }
                      loadOptions={loadCategoryOptions}
                      additional={{ page: 1 }}
                      onChange={(selected) => {
                        if (selected) {
                          formik.setFieldValue("category", selected.value, true);
                          setSelectedCategories([selected.value]);
                        } else {
                          formik.setFieldValue("category", "", true);
                          setSelectedCategories([]);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("category", true)}
                      isClearable
                      key={selectedBrand || "no-brand"}
                      className={formik.touched?.category && formik.errors?.category ? "is-invalid" : ""}
                      styles={{
                        control: (base: any) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: formik.touched?.category && formik.errors?.category ? "#dc3545" : base.borderColor,
                        }),
                      }}
                    />
                    {formik.touched?.category && formik.errors?.category && (
                      <div className="invalid-feedback d-block">{formik.errors.category}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Product Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control product-description-textarea"
                      rows={4}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                      placeholder="Enter product description..."
                    />
                  </div>

                  <div className="p-3 mb-3">
                    <div className="row g-2">
                      <div className="col-4">
                        <UIInputFF
                          formik={formik}
                          name="threshold_qty"
                          label="Threshold Quantity"
                          type="number"
                          placeholder="0"
                          hideZero
                        />
                      </div>
                      <div className="col-4">
                        <UIReactSelect
                          formik={formik}
                          name="is_production"
                          placeholder="Select"
                          label=" Is Production"
                          required
                          options={[
                            // { value: "", label: "- Select -" },
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" },
                          ]}
                        />
                      </div>
                      <div className="col-4">
                        <UIReactSelect
                          formik={formik}
                          name="statuss"
                          label="Status"
                          options={[
                            { value: "Active", label: "Active" },
                            { value: "Inactive", label: "Inactive" },
                          ]}
                          placeholder="Select"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT */}
                <div className="col-lg-6">
                  <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                    <i className="bi bi-currency-dollar pe-1" />
                    SKU & Pricing
                  </div>

                  <div className="mb-3">
                    <UIInputFF formik={formik} name="sku" label="SKU" required placeholder="Enter SKU" />
                  </div>

                  <div className="mb-3">
                    <UIInputFF formik={formik} name="purchase_price" label="Purchase Price" placeholder="0.00" type="number"  />
                  </div>

                  <div className="mb-4">
                    <UIInputFF formik={formik} name="retail_price" label="Retail Price" required placeholder="0.00" type="number" />
                  </div>

                  <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                    <i className="bi bi-paperclip pe-1" />
                    Physical Properties
                  </div>

                  {/* Shipping & Dimensions */}
                  <div className="border rounded-4 p-3 mb-3">
                    <div className="fw-semibold mb-2">Shipping &amp; Dimensions</div>

                    <div className="mb-2 small">Shipping Configuration</div>

                    <div className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="ship-global"
                        name="shipping_config"
                        value="global"
                        checked={formik.values.shipping_config === "global"}
                        onChange={() => {
                          formik.setFieldValue("shipping_config", "global");
                          // zero parent dims (shown in UI)
                          resetGlobalDimsToZero();
                          // optional: also zero any per-variant dims you may have edited earlier
                          resetAllCombinationDimsToZero();
                        }}
                      />
                      <label className="form-check-label" htmlFor="ship-global">
                        Same shipping for all variants
                      </label>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="ship-per-variant"
                        name="shipping_config"
                        value="per_variant"
                        checked={formik.values.shipping_config === "per_variant"}
                        onChange={() => {
                          formik.setFieldValue("shipping_config", "per_variant");
                          // clear the parent dims (they won’t be used now)
                          resetGlobalDimsToZero();
                          // and zero per-variant dims so user starts fresh in Combinations edit
                          resetAllCombinationDimsToZero();
                        }}
                      />
                      <label className="form-check-label" htmlFor="ship-per-variant">
                        Different shipping per variant
                      </label>
                    </div>


                    {formik.values.shipping_config === "global" ? (
                      <>
                        <div className="mb-2 small text-muted">Dimensions/Measurements (Optional)</div>
                        <div className="row g-2">
                          <div className="col-4">
                            <UIInputFF formik={formik} name="length" type="number" label="Length(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-4">
                            <UIInputFF formik={formik} name="width" type="number" label="Width(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-4">
                            <UIInputFF formik={formik} name="height" type="number" label="Height(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-6">
                            <UIInputFF formik={formik} name="weight" type="number" label="Weight(lb)" placeholder="0.0" hideZero />
                          </div>
                        </div>
                      </>

                    ) : (
                      <div className="text-muted">
                        Dimensions will be configured individually for each variant in the Combinations tab.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <UIReactSelect
                      formik={formik}
                      name="item_condition"
                      label="Item Condition"
                      placeholder="Brand New"
                      options={[
                        { value: "new", label: "New" },
                        { value: "used", label: "Used" },
                        { value: "damaged", label: "Damaged" },
                      ]}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Condition Notes</label>
                    <textarea
                      id="condition_note"
                      name="condition_note"
                      className="form-control"
                      rows={4}
                      value={formik.values.condition_note}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                      placeholder="Enter condition notes..."
                    />
                  </div>

                  <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                    <div {...iconLabelProps(16)}>
                      <Settings {...withIconStyle("#4a5565", 19)} />
                      <span className="header-color-product"> Tax Settings</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <input
                      type="checkbox"
                      name="is_taxable"
                      id="is_taxable"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.is_taxable}
                      className="ms-2"
                    />
                    <label className="form-label ms-2">Is Taxable</label>
                  </div>

                  {/* Keeping native <select> here to preserve your extra side-effects */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Tax Rule</label>
                    <select
                      className="form-control"
                      id="tax_rule_id"
                      name="tax_rule_id"
                      onChange={(e) => {
                        const value = e.target.value;
                        formik.setFieldValue("tax_rule_id", value);
                        if (value.startsWith("rule-")) {
                          const ruleId = value.replace("rule-", "");
                          const selectedRule = taxRules?.find((r: any) => r._id === ruleId);
                          if (selectedRule) formik.setFieldValue("taxRule", selectedRule);
                        } else if (value.startsWith("group-")) {
                          const groupId = value.replace("group-", "");
                          const selectedGroup = taxGroups?.find((g: any) => g.id === groupId);
                          if (selectedGroup) formik.setFieldValue("taxGroup", selectedGroup);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.tax_rule_id}
                      disabled={!formik.values.is_taxable}
                    >
                      <option value="">- Select -</option>
                      {taxGroups?.map((group: any) => (
                        <option key={`group-${group.id}`} value={`group-${group.id}`} style={{ fontWeight: "bold" }}>
                          {group.name}
                        </option>
                      ))}
                      {taxRules?.map((rule: any) => (
                        <option key={`rule-${rule._id}`} value={`rule-${rule._id}`}>
                          {`${rule.country} - ${rule.taxRate}%`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="col-12 mb-2" id="row-overlap">
                <hr className="" />
              </div>
              <div className="d-flex justify-content-between align-items-center mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => safeNavigate(-1)}>
                  <i className="bi bi-chevron-left me-2" />
                  Back
                </button>

                {/* 👉 Next goes to Combinations tab */}
                {!isProductDetailsFilled ? (
                  <button type="submit" className="btn btn-success save-btn surface-btn fw-bold fs-16">
                    Next
                  </button>
                ) : combinations.length === 0 ? (
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton save-btn surface-btn fw-bold fs-16"
                    onClick={() => {
                      const tabEl = document.querySelector('a[href="#combinations"]') as HTMLElement;
                      tabEl?.click();
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <div className="mt-3 mb-3 d-flex justify-content-end">
                    <input
                      type="hidden"
                      id="product_type"
                      name="product_type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.product_type}
                    />
                    <button className="btn btn-primary table-addbutton save-btn surface-btn fw-bold fs-16" type="submit">
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ================= IMAGES ================= */}
          <div className="tab-pane fade" id="images" role="tabpanel">
            <div className="card shadow-border-2">
              <div className="card-body">
                <h6 className="mb-3">
                  Images ({uploaded.length + pendingFiles.length})
                  {pendingFiles.length > 0 && (
                    <span className="text-muted"> • {pendingFiles.length} Pending</span>
                  )}
                </h6>

                <div className="imageuploadui" onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0}>
                  <i className="bi bi-cloud-arrow-up fs-2 d-block mb-2" />
                  <div className="fw-semibold">Upload Images</div>
                  <div className="small text-muted">Drag & drop images here, or click to select files</div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    onPickFiles(files); // ✅ Now just adds to preview, doesn't upload
                    e.currentTarget.value = "";
                  }}
                />

                <div className="row mt-3">
                  {uploaded.length === 0 && pendingFiles.length === 0 ? (
                    <div className="col-12 text-center text-muted">
                      <i className="bi bi-image fs-2 d-block mb-2" />
                      No images added yet
                    </div>
                  ) : (
                    <>
                      {/* ✅ Show already uploaded images */}
                      {uploaded.map((img, i) => {
                        const src = getImageSrc(img.id, imageUrlMap);
                        return (
                          <div key={img.id} className="col-6 col-md-4 col-lg-3 mb-3">
                            <div className="img-card">
                              <button
                                type="button"
                                className="btn-close img-x"
                                aria-label="Remove"
                                onClick={() => removeUploadedImage(img.id)}
                              />
                              {src ? (
                                <img className="img-thumb" src={src} alt={img.name} />
                              ) : (
                                <div className="text-muted small">Loading…</div>
                              )}
                              {/* ✅ Badge to show it's already uploaded */}
                              <span className="img-badge uploaded-badge">Uploaded</span>
                            </div>
                            <div className="small text-center mt-1 text-muted text-truncate">{img.name}</div>
                          </div>
                        );
                      })}

                      {/* ✅ Show pending preview images */}
                      {pendingFiles.map((pending, i) => (
                        <div key={pending.previewUrl} className="col-6 col-md-4 col-lg-3 mb-3">
                          <div className="img-card">
                            <button
                              type="button"
                              className="btn-close img-x"
                              aria-label="Remove"
                              onClick={() => removePendingImage(pending.previewUrl)}
                            />
                            <img className="img-thumb" src={pending.previewUrl} alt={pending.file.name} />
                            {/* ✅ Badge to show it's pending upload */}
                            <span className="img-badge pending-badge">Pending Upload</span>
                          </div>
                          <div className="small text-center mt-1 text-muted text-truncate">{pending.file.name}</div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
              {/* FOOTER — only show after at least one pending image */}
              {pendingFiles.length > 0 && (
                <div className="card-footer bg-transparent">
                  <div className="d-flex justify-content-start align-items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-light d-inline-flex align-items-center gap-2 rounded-4"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="bi bi-cloud-arrow-up" />
                      Upload More Images
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary table-addbutton"
                      onClick={handlePrepareProductImages}
                      disabled={imagesPreparing}
                      title="Upload selected images and attach to this product"
                    >
                      {imagesPreparing ? "Saving…" : "Save Images"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ================= COMBINATIONS ================= */}
          <div className="tab-pane fade" id="combinations" role="tabpanel">
            {!isEditMode && (
              <div className="mt-2">
                <div className="row g-4">
                  {/* LEFT: combinations list */}
                  <div className="col-lg-9">
                    <h5 className="mb-3">Combinations List</h5>

                    {/* Desktop table */}
                    <div className="rdt-desktop">
                      <div className="card box-shadow-datatables shadow-border-2">
                        <div className="" style={{ maxHeight: 420, overflowY: "auto" }}>
                          <table className="table align-middle mb-0">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: "12%", paddingLeft: "15px" }} >SNO</th>
                                <th>Combination</th>
                                <th style={{ width: 160 }}>Price</th>
                                <th style={{ width: 80 }}>Edit</th>
                                <th style={{ width: 90 }}>Remove</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinations.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-center text-muted">No combinations added yet.</td>
                                </tr>
                              ) : (
                                combinations.map((combination: any, idx: number) => (
                                  <tr key={idx}>
                                    <td style={{ paddingLeft: "15px" }}>{idx + 1}</td>
                                    <td className="text-wrap">
                                      {/* attributes text */}
                                      {Object.entries(combination.attributes).map(([k, v]) => `${k}: ${v}`).join(", ")}

                                      {/* === Only selected images === */}
                                      {(() => {
                                        // 1) Collect selected thumbs (prefer uploaded IDs; fall back to index selection)
                                        const byIds = Array.isArray(combination.imageIds) ? combination.imageIds : [];
                                        const byIdx = Array.isArray((combination as any).imageIndexes)
                                          ? (combination as any).imageIndexes
                                          : [];

                                        // If you have uploaded list (id-based)
                                        let selectedThumbs: Array<{ key: string | number; src: string | undefined; title?: string }> = [];
                                        if (uploaded && uploaded.length && byIds.length) {
                                          selectedThumbs = uploaded
                                            .filter((u: any) => byIds.includes(u.id))
                                            .map((u: any) => ({
                                              key: u.id,
                                              src: getImageSrc(u.id, imageUrlMap),
                                              title: u.name,
                                            }));
                                        } else if (previewFiles && previewFiles.length && byIdx.length) {
                                          // Fallback: index-based (before upload)
                                          selectedThumbs = byIdx
                                            .map((ix: number) => ({
                                              key: ix,
                                              src: previewFiles[ix]?.url,
                                              title: previewFiles[ix]?.file?.name,
                                            }))
                                          // .filter(t => !!t.src);
                                        }

                                        const count = selectedThumbs.length;

                                        return (
                                          <>
                                            <div className="mini-gallery mt-2">
                                              {count > 0 ? (
                                                <div className="d-flex align-items-center gap-2">
                                                  {/* optional default puck */}
                                                  {/* <span className="mini-default-badge">✓</span> */}

                                                  {selectedThumbs?.map((t: any) => (
                                                    <span key={t.key} className="mini-thumb is-selected" title={t.title || ""}>
                                                      <img src={t.src!} alt="" />
                                                    </span>
                                                  ))}
                                                </div>
                                              ) : (
                                                <span className="small text-muted"></span>
                                              )}
                                            </div>

                                            <div className="small text-muted mt-1">
                                              {count > 0 ? `${count} image${count > 1 ? "s" : ""} selected` : "No images selected"}
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="0"
                                        value={combination.price == 0 ? "" : combination.price}
                                        onChange={(e) => {
                                          const price = parseFloat(e.target.value) || 0;
                                          setCombinations((prev: any[]) =>
                                            prev.map((c, i) => (i === idx ? { ...c, price } : c))
                                          );
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <EditIconButton
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={() => {
                                          setIsEditMode(true);
                                          setEditIndex(idx);
                                          // Initialize temp imageIds with current values
                                          setTempImageIds([...(combinations[idx]?.imageIds || [])]);
                                        }}
                                        title="Edit"
                                      />
                                    </td>
                                    <td>
                                      {(() => {
                                        const delKey = String(combination?.id ?? idx); // normalize to string
                                        return (
                                          <DeleteIconButton
                                            type="button"
                                            className="btn btn-link text-danger p-0"
                                            title="Remove"
                                            disabled={deletingIds.has(delKey)}
                                            onClick={async () => {
                                              // compute the id at click time (trim to avoid hidden whitespace)
                                              // const variantId = String(row?.variant_id ?? row?.id ?? "").trim();
                                              // safeConsole.log("Deleting variant:", { idx, variantId, row });

                                              // // if there is no server id at all, just fail gracefully
                                              // if (!variantId) {
                                              //   showError?.("Cannot delete: missing variant id.");
                                              //   return;
                                              // }

                                              setDeletingIds(prev => new Set(prev).add(delKey));
                                              try {
                                                // 🔥 call your API directly
                                                await deleteVariantFromServer(delKey);

                                                // remove locally after server success
                                                setCombinations(prev => prev.filter((_, i) => i !== idx));

                                                // optional: re-sync from backend
                                                // await fetchProduct();

                                                showSuccess?.("Variant deleted.");
                                              } catch (err: any) {
                                                safeConsole.error(err);
                                                showError?.(err?.message || "Failed to delete variant.");
                                              } finally {
                                                setDeletingIds(prev => {
                                                  const n = new Set(prev);
                                                  n.delete(delKey);
                                                  return n;
                                                });
                                              }
                                            }}
                                          >
                                            {deletingIds.has(delKey) ? (
                                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            ) : (
                                              <i className="bi bi-trash" />
                                            )}
                                          </DeleteIconButton>

                                        );
                                      })()}
                                    </td>

                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Mobile cards (<= 1021px) */}
                    {/* Mobile cards (<= 1021px) */}
                    <div className="rdt-mobile">
                      <MobileCardsOnly table={mobileTable} />
                    </div>

                  </div>

                  {/* RIGHT: attributes + Generate */}
                  <div className="col-lg-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h5 className="mb-0">Attributes</h5>
                      <button type="button" className="btn btn-primary" onClick={handleGenerateAndCreate}>
                        Generate
                      </button>
                    </div>

                    <div className="card shadow-border-2 box-shadow-datatables p-3">
                      <div style={{ maxHeight: 520, overflowY: "auto" }}>
                        {attributes?.map((attr: any, index: number) => (
                          <div key={index} className="mb-4">
                            <div className="fw-bold text-secondary mb-2">{attr.name}</div>
                            <ul className="list-group">
                              {attr.values?.map((param: string, pIdx: number) => {
                                const selected = options.some((opt: any) => opt[attr.name.toLowerCase()] === param);
                                return (
                                  <li
                                    key={pIdx}
                                    className={`list-group-item list-group-item-action ${selected ? "active" : ""}`}
                                    onClick={() => handleAttributeClick(attr.name, param)}
                                    style={{
                                      cursor: "pointer",
                                      backgroundColor: selected ? "#dcfce7" : "#fff",
                                      color: selected ? "#016630" : "#364153",
                                      border: selected ? "1px solid #7bf1a8" : "1px solid #364153",
                                      transition: "all .2s",
                                    }}
                                  >
                                    {param}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ====== EDIT MODE (sticky save) ====== */}
            {isEditMode && editIndex !== null && (
              <div className="mt-2">
                <div className="d-flex align-items-center mb-3">
                  <button
                    type="button"
                    className="btn btn-light fw-bold"
                    onClick={() => {
                      setIsEditMode(false);
                      setEditIndex(null);
                      setTempImageIds(null); // Clear temp state when canceling
                    }}
                  >
                    <i className="bi bi-chevron-left me-2" />
                    Back to Combinations
                  </button>
                </div>

                <div className="card shadow-border-2 box-shadow-datatables p-3">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-5">
                        <UIInputFF
                          formik={{
                            values: {
                              tmpName: Object.entries(combinations[editIndex].attributes)
                                .map(([k, v]: any) => `${k}: ${v}`).join(", ")
                            },
                            handleChange: () => { },
                            handleBlur: () => { },
                          } as any}
                          name="tmpName"
                          label="Product Name"
                          required
                          placeholder=""
                        />
                      </div>
                      <div className="col-md-3">
                        <UIInputFF
                          formik={{
                            values: { tmpSku: combinations[editIndex]?.sku || "" },
                            errors: {},
                            touched: {},
                            handleChange: (e: any) =>
                              setCombinations((prev: any[]) =>
                                prev.map((c, i) => (i === editIndex ? { ...c, sku: e.target.value.trim() } : c))
                              ),
                            handleBlur: () => { },
                            setFieldValue: (_field: string, value: any) => {
                              setCombinations((prev: any[]) =>
                                prev.map((c, i) => (i === editIndex ? { ...c, sku: String(value || "").trim() } : c))
                              );
                            },
                            setFieldTouched: () => { },
                          } as any}
                          name="tmpSku"
                          label="SKU"
                          placeholder="SKU"
                        />
                      </div>
                      {/* <div className="col-md-4">
                        <UIInputFF
                          formik={{
                            values: { tmpPrice: combinations[editIndex]?.price || 0 },
                            handleChange: (e: any) => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              setCombinations((prev: any[]) =>
                                prev.map((c, i) => (i === editIndex ? { ...c, price: newPrice } : c))
                              );
                            },
                            handleBlur: () => { },
                          } as any}
                          name="tmpPrice"
                          label="Price"
                          placeholder="0"
                          type="number"
                        />
                      </div> */}

                      <div className="col-md-4">
                        <UIInputFF
                          formik={{
                            values: { tmpPrice: combinations[editIndex]?.price ?? 0 },
                            errors: {},
                            touched: {},
                            handleBlur: () => { },
                            handleChange: (e: any) => {
                              const v = parseFloat(e?.target?.value ?? e) || 0;
                              setCombinations(prev => prev.map((c, i) => (i === editIndex ? { ...c, price: v } : c)));
                            },
                            setFieldValue: (_field: string, value: any) => {
                              const v = parseFloat(value) || 0;
                              setCombinations(prev => prev.map((c, i) => (i === editIndex ? { ...c, price: v } : c)));
                            },
                            setFieldTouched: () => { },
                          } as any}
                          name="tmpPrice"
                          label="Price"
                          placeholder="0"
                          type="number"
                          hideZero
                        />
                      </div>
                      {isEditMode && editIndex !== null && (
                        <div className="combo-picker rounded-3 p-3 mb-3">
                          <div className="fw-semibold mb-2">Select Images for this Combination</div>
                          <div className="small text-muted mb-2">
                            Choose one or more images to associate with this product combination
                          </div>

                          <div className="d-flex flex-wrap gap-3">
                            {uploaded.length === 0 && pendingFiles.length === 0 ? (
                              <div className="text-muted">No images available. Use the upload box in the Images tab.</div>
                            ) : (
                              <>
                                {/* ✅ Show uploaded images */}
                                {uploaded.map((u) => {
                                  // Use tempImageIds if in edit mode, otherwise use actual imageIds
                                  const currentImageIds = tempImageIds !== null ? tempImageIds : (combinations[editIndex]?.imageIds || []);
                                  const selected = currentImageIds.includes(u.id);
                                  const src = getImageSrc(u.id, imageUrlMap);

                                  return (
                                    <button
                                      type="button"
                                      key={u.id}
                                      className={`combo-thumb ${selected ? "is-selected" : ""}`}
                                      onClick={() => {
                                        // Update tempImageIds instead of directly modifying combinations
                                        setTempImageIds((prev) => {
                                          const current = prev || (combinations[editIndex]?.imageIds || []);
                                          const set = new Set(current);
                                          set.has(u.id) ? set.delete(u.id) : set.add(u.id);
                                          return Array.from(set);
                                        });
                                      }}
                                      title={selected ? "Unselect image" : "Select image"}
                                    >
                                      <div className="combo-thumb-inner">
                                        {src ? (
                                          <img src={src} alt={u.name} />
                                        ) : (
                                          <div className="d-flex align-items-center justify-content-center text-muted small" style={{ height: 100 }}>
                                            Loading…
                                          </div>
                                        )}
                                      </div>
                                      {selected && <span className="combo-check">✓</span>}
                                    </button>
                                  );
                                })}

                                {/* ✅ Show pending images (can't be selected until uploaded) */}
                                {pendingFiles.map((pending) => (
                                  <div
                                    key={pending.previewUrl}
                                    className="combo-thumb opacity-50"
                                    title="This image will be available for selection after you save the product"
                                  >
                                    <div className="combo-thumb-inner">
                                      <img src={pending.previewUrl} alt={pending.file.name} />
                                    </div>
                                    <span className="combo-pending-label">Pending Upload</span>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>

                          <div className="small mt-2 text-primary">
                            {(tempImageIds !== null ? tempImageIds : (combinations[editIndex]?.imageIds || [])).length} image
                            {(tempImageIds !== null ? tempImageIds : (combinations[editIndex]?.imageIds || [])).length !== 1 ? "s" : ""} selected
                          </div>
                          {pendingFiles.length > 0 && (
                            <div className="small mt-1 text-warning">
                              ⚠️ {pendingFiles.length} image{pendingFiles.length !== 1 ? "s" : ""} pending upload. Save the product to upload them.
                            </div>
                          )}
                        </div>
                      )}
                      {formik.values.shipping_config === "per_variant" && editIndex !== null && (
                        <div className="dim-card p-3 my-2">
                          <div className="dim-title">Dimensions &amp; Weight</div>

                          <div className="row g-3">
                            <div className="col-md-3">
                              <label className="form-label dim-label">Length (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].length ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, length: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Width (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].width ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, width: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Height (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].height ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, height: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Weight (lb)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].weight ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, weight: v } : c))
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="col-12">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={combinations[editIndex].description || ""}
                          onChange={(e) =>
                            setCombinations((prev: any[]) =>
                              prev.map((c, i) => (i === editIndex ? { ...c, description: e.target.value } : c))
                            )
                          }
                        />
                      </div>
                    </div>

                    <hr className="my-4" />
                    <h6 className="mb-2">Stock Details:</h6>

                    {/* header */}
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <colgroup>
                          <col style={{ width: "12%" }} />  {/* Warehouse */}
                          <col style={{ width: "7%" }} />  {/* Available */}
                          <col style={{ width: "7%" }} />  {/* Zone */}
                          <col style={{ width: "7%" }} />  {/* Rack */}
                          <col style={{ width: "7%" }} />  {/* Bin Location */}
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">Warehouse</th>
                            <th scope="col">Available</th>
                            <th scope="col">Zone</th>
                            <th scope="col">Rack</th>
                            <th scope="col">Bin Location</th>
                          </tr>
                        </thead>

                      </table>
                    </div>

                    {/* rows */}
                    <div style={{ maxHeight: 420, overflowY: "auto" }}>
                      {combinations[editIndex].warehouseData.map((row: any, wi: number) => (
                        <div key={`${editIndex}-${row.warehouse_id}`} className="rounded-4 border p-3 mt-2 bg-white shadow-sm">
                          <div className="row g-3">
                            <div className="col-xl-3 col-md-6">
                              <input className="form-control" readOnly value={row.warehouse_name} />
                            </div>

                            <div className="col-xl-2 col-md-6">
                              <input
                                type="number"
                                min={0}
                                className="form-control"
                                value={row.available === 0 ? "" : row.available}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setCombinations((prev: any[]) =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? { ...c, warehouseData: c.warehouseData.map((w: any, j: number) => (j === wi ? { ...w, available: val, on_hand: val } : w)) }
                                        : c
                                    )
                                  );
                                  setCombErrors((prev: any[][]) => {
                                    const copy = prev.map((arr) => arr.slice());
                                    copy[editIndex][wi] = { zone: false, rack: false, bin: false };
                                    return copy;
                                  });
                                }}
                              />
                            </div>

                            {/* ZONE */}
                            <div className="col-xl-2 col-md-6">
                              <select
                                disabled={!(row.available > 0)}
                                className={`form-control ${combErrors[editIndex][wi].zone ? "is-invalid" : ""}`}
                                value={row.zone_id}
                                onChange={(e) => {
                                  const opt = e.currentTarget.selectedOptions[0];
                                  const value = opt?.value || "";
                                  const label = opt?.text || "";

                                  // update zone + reset rack/bin
                                  setCombinations(prev =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? {
                                          ...c,
                                          warehouseData: c.warehouseData.map((w, j) =>
                                            j === wi
                                              ? {
                                                ...w,
                                                zone_id: value,
                                                zone_name: label,
                                                rack_id: "",
                                                rack_name: "",
                                                bin_id: "",
                                                bin_name: "",
                                              }
                                              : w
                                          ),
                                        }
                                        : c
                                    )
                                  );

                                  // clear zone error
                                  setCombErrors(prev => {
                                    const copy = prev.map(arr => arr.slice());
                                    copy[editIndex][wi].zone = false;
                                    // also clear downstream since we reset them
                                    copy[editIndex][wi].rack = false;
                                    copy[editIndex][wi].bin = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Zone</option>
                                {(locationOptions[row.warehouse_id]?.zones || []).map((z: any) => (
                                  <option key={z._id} value={z._id}>{z.label}</option>
                                ))}
                              </select>
                            </div>

                            {/* RACK */}
                            <div className="col-xl-2 col-md-6">
                              <select
                                disabled={!(row.available > 0) || !row.zone_id}
                                className={`form-control ${combErrors[editIndex][wi].rack ? "is-invalid" : ""}`}
                                value={row.rack_id}
                                onChange={(e) => {
                                  const opt = e.currentTarget.selectedOptions[0];
                                  const value = opt?.value || "";
                                  const label = opt?.text || "";

                                  // update rack + reset bin
                                  setCombinations(prev =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? {
                                          ...c,
                                          warehouseData: c.warehouseData.map((w, j) =>
                                            j === wi
                                              ? {
                                                ...w,
                                                rack_id: value,
                                                rack_name: label,
                                                bin_id: "",
                                                bin_name: "",
                                              }
                                              : w
                                          ),
                                        }
                                        : c
                                    )
                                  );

                                  // clear rack error
                                  setCombErrors(prev => {
                                    const copy = prev.map(arr => arr.slice());
                                    copy[editIndex][wi].rack = false;
                                    // also clear bin since we reset it
                                    copy[editIndex][wi].bin = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Rack</option>
                                {(locationOptions[row.warehouse_id]?.racks || [])
                                  .filter((r: any) => r.zone_id === row.zone_id)
                                  .map((r: any) => (
                                    <option key={r._id} value={r._id}>{r.label}</option>
                                  ))}
                              </select>
                            </div>

                            {/* BIN */}
                            <div className="col-xl-3 col-md-6">
                              <select
                                disabled={!(row.available > 0) || !row.rack_id}
                                className={`form-control ${combErrors[editIndex][wi].bin ? "is-invalid" : ""}`}
                                value={row.bin_id}
                                onChange={(e) => {
                                  const opt = e.currentTarget.selectedOptions[0];
                                  const value = opt?.value || "";
                                  const label = opt?.text || "";

                                  setCombinations(prev =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? {
                                          ...c,
                                          warehouseData: c.warehouseData.map((w, j) =>
                                            j === wi ? { ...w, bin_id: value, bin_name: label } : w
                                          ),
                                        }
                                        : c
                                    )
                                  );

                                  // clear bin error
                                  setCombErrors(prev => {
                                    const copy = prev.map(arr => arr.slice());
                                    copy[editIndex][wi].bin = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Bin</option>
                                {(locationOptions[row.warehouse_id]?.bins || [])
                                  .filter((b: any) => b.rack_id === row.rack_id)
                                  .map((b: any) => (
                                    <option key={b._id} value={b._id}>{b.label}</option>
                                  ))}
                              </select>
                            </div>

                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* sticky bottom action bar */}
                  <div className="position-sticky bottom-0 bg-white border-top d-flex justify-content-end p-3">
                    <button
                      type="button"
                      className="btn btn-primary table-addbutton save-btn surface-btn fw-bold fs-16"
                      onClick={async () => {
                        const errors = combinations.map((c: any) => c.warehouseData.map(() => ({ zone: false, rack: false, bin: false })));
                        let hasError = false;
                        const hasAnyQty = combinations[editIndex].warehouseData.some((r: any) => r.available > 0);
                        if (!hasAnyQty) {
                          showInfo("Enter quantity for at least one warehouse.");
                          return;
                        }
                        combinations[editIndex].warehouseData.forEach((row: any, wi: number) => {
                          if (row.available > 0) {
                            if (!row.zone_id) { errors[editIndex][wi].zone = true; hasError = true; }
                            if (!row.rack_id) { errors[editIndex][wi].rack = true; hasError = true; }
                            if (!row.bin_id) { errors[editIndex][wi].bin = true; hasError = true; }
                          }
                        });
                        if (hasError) {
                          showInfo("Please select a zone, rack and bin for your selected warehouse.");
                          setCombErrors(errors);
                          return;
                        }

                        if (editIndex === null) return;

                        // Create updated combination with tempImageIds applied
                        const updatedCombination = {
                          ...combinations[editIndex],
                          imageIds: tempImageIds !== null ? tempImageIds : (combinations[editIndex]?.imageIds || [])
                        };

                        // Update state with the new imageIds
                        setCombinations((prev) =>
                          prev.map((c, i) =>
                            i === editIndex ? updatedCombination : c
                          )
                        );

                        // Save using the updated combination
                        const rowUI = updatedCombination as any;
                        if (!token || !signageId) {
                          showError("Not authenticated");
                          return;
                        }
                        if (!productId) {
                          showError("Missing product");
                          return;
                        }
                        if (!rowUI?.id) {
                          showError("This combination is not linked to a variant yet. Generate variants first.");
                          return;
                        }

                        const payload = mapCombinationRowToVariantPayload(updatedCombination, formik.values);
                        try {
                          const res = await fetch(
                            `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${rowUI.id}`,
                            {
                              method: "PATCH",
                              headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                              },
                              body: JSON.stringify(payload),
                            }
                          );
                          const result = await res.json();
                          if (!res.ok || result?.error) {
                            const msg = result?.error || result?.message || "Failed to update variant";
                            throw new Error(msg);
                          }

                          showSuccess("Combination saved.");
                          setIsEditMode(false);
                          setEditIndex(null);
                          setTempImageIds(null); // Clear temp state
                        } catch (e: any) {
                          await handleApiError(e, {
                            module: "inventory",
                            action: "Signage_Edit_Variable_Product_update_variant",
                            userId: signageId || undefined,
                            licenseType: licenseType || undefined,
                            userName: signageName || undefined,
                            url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/${productId}/variants/${rowUI.id}`,
                            method: "PATCH",
                            requestBody: payload,
                          }, token);
                          safeConsole.error(e);
                          showError(e?.message || "Failed to save combination");
                        }
                      }}
                    >
                      Save Combination
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* <div className="tab-pane fade" id="volume-discount" role="tabpanel">
              <SignageVolumeDiscount
                productType={formik.values.product_type === "variable" ? "variable" : "simple"}
                combinations={variantCombos}
                value={volumeDiscounts}
                onChange={setVolumeDiscounts}
              />
            </div> */}
          <div className="tab-pane fade" id="volume-discount" role="tabpanel">
            {productId ? (
              <SignageVolumeDiscount
                productType={formik.values.product_type === "variable" ? "variable" : "simple"}
                combinations={combosForDiscount}     // ← normalized with id: string
                value={volumeDiscounts}
                onChange={setVolumeDiscounts} // ← adapter, not the bare setter
                productId={productId}
                token={token}
              />
            ) : (
              <div className="alert alert-info">
                Save the product first, then open this page in Edit mode to manage volume discounts.
              </div>
            )}
          </div>




          {/* ================= CUSTOMIZATION ================= */}
          <div className="tab-pane fade" id="customization" role="tabpanel">
            <div className="card shadow-border-2 box-shadow-datatables p-3">
              <div className="card-body">
                <CustomFieldBuilder onSave={(fields) => setCustomFields(fields)} initialFields={customFields} />
              </div>
            </div>
          </div>

          {/* ================= DESIGN ================= */}
          <div className="tab-pane fade" id="design" role="tabpanel">
            {productId && variables.length > 0 ? (
              <DesignsTab
                productType="variable"
                mode="edit"
                combinations={variables}
                productId={productId}
              />
            ) : (
              <div className="alert alert-info">
                {!productId
                  ? "Product information is missing. Please refresh the page."
                  : "No variants available. Please create variants first."}
              </div>
            )}
          </div>
        </div>
      </form>

      {/* styles (tabs color + responsiveness) */}
      <style>{`
      .rc-tabs .activetabs{
        border:1px solid #e5e7eb;background:#f8f9fb;padding:10px 16px;border-radius:12px;
        font-weight:600;color:#374151;box-shadow:0 2px 10px rgba(0,0,0,.03)
      }
      .rc-tabs .activetabs:hover{background:#eef2ff}
      .rc-tabs .activetabs.disabled{pointer-events:none;opacity:.5}

      /* different active bg per tab */
      .rc-tabs a.activetabs.active[href="#product-details"]{background:#2563eb;color:#fff;border-color:#2563eb;box-shadow:0 6px 18px rgba(37,99,235,.18)}
      .rc-tabs a.activetabs.active[href="#images"]{background:#7c3aed;color:#fff;border-color:#7c3aed;box-shadow:0 6px 18px rgba(124,58,237,.18)}
      .rc-tabs a.activetabs.active[href="#combinations"]{background:#16a34a;color:#fff;border-color:#16a34a;box-shadow:0 6px 18px rgba(22,163,74,.18)}
      .rc-tabs a.activetabs.active[href="#volume-discount"]{background:#2563eb;color:#fff;border-color:#2563eb;box-shadow:0 6px 18px rgba(37,99,235,.18)}
      .rc-tabs a.activetabs.active[href="#customization"]{background:#f97316;color:#fff;border-color:#f97316;box-shadow:0 6px 18px rgba(249,115,22,.18)}
      .rc-tabs a.activetabs.active[href="#design"]{background:#ec4899;color:#fff;border-color:#ec4899;box-shadow:0 6px 18px rgba(236,72,153,.18)}

      /* desktop vs mobile */
      .rdt-desktop{display:block}
      .rdt-mobile{display:none}
      @media (max-width:1021px){.rdt-desktop{display:none}.rdt-mobile{display:block}}

      /* Image upload and preview styles */
      .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
      .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
      .uploadedimages{border-radius:10px}
        /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fits nicely without changing card height */
.img-thumb{
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
}

/* Close button perfectly aligned inside the tile */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}
  
/* --- Dimensions card --- */
.dim-card {
  background: #f6f7fb;               /* soft panel bg */
  border: 1px solid rgba(2,6,23,.06); /* subtle border */
  border-radius: 14px;
}

/* title inside the panel */
.dim-card .dim-title {
  font-weight: 600;
  margin-bottom: 10px;
}

/* --- Inputs look like screenshot --- */
.form-control.dim-input {
  background-color: #f1f5f9 !important; /* light input bg */
  border: 1px solid #e5e7eb !important; /* soft border */
  border-radius: 12px !important;
  padding: 10px 12px;
  box-shadow: none !important;
}

.form-control.dim-input::placeholder {
  color: #9ca3af; /* muted placeholder (0.0) */
}

/* Focus ring (subtle blue) */
.form-control.dim-input:focus {
  border-color: #bfdbfe !important;
  box-shadow: 0 0 0 3px #dbeafe !important; /* light ring */
  background-color: #f8fafc !important;
}

/* Labels spacing like screenshot */
.dim-label {
  font-weight: 600;
  margin-bottom: 6px;
  color: #1f2937;
}


/* ===== Combination Image Picker (edit panel) ===== */
.combo-picker {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.combo-thumb {
  position: relative;
  width: 150px;
  padding: 0;
  border: 0;
  background: transparent;
}

.combo-thumb-inner {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 6px;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease, transform .05s ease;
}

.combo-thumb-inner:hover {
  transform: translateY(-1px);
}

.combo-thumb img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
}

.combo-thumb.is-selected .combo-thumb-inner {
  border-color: #3b82f6; /* blue */
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

/* top-right check badge like your screenshot */
.combo-check {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #3b82f6;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

/* ===== Mini gallery inside combinations table cell ===== */
.mini-gallery {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-default-badge {
  width: 18px; height: 18px;
  border-radius: 9999px;
  background: #3b82f6;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.mini-thumb {
  width: 28px; height: 28px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.mini-thumb.is-selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59,130,246,.15);
}

.mini-thumb img {
  width: 100%; height: 100%; object-fit: contain;
}


/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }

/* ===== Image status badges ===== */
.img-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  z-index: 2;
}

.uploaded-badge {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.pending-badge {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

/* ===== Pending upload label for combo picker ===== */
.combo-pending-label {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  background: rgba(251, 191, 36, 0.95);
  color: #78350f;
  z-index: 3;
  white-space: nowrap;
}
    `}</style>
    </div>
  );

};

export default SignageEditVariableProduct;
